import axios from 'axios';
import { beginRequestPerformance, endRequestAuth, finishRequestPerformance } from './requestPerformance';
import { resetCoachSequenceForIdentity } from './coachSequenceTiming';
import { getCachedCompletionPosition } from '../components/activities/activityPosition';
import { runMealSuggestionRequest } from './mealSuggestionJobs';
import { requestProgressImage } from './progressImage';
import { API_BASE_URL } from '../config';
import WorkoutSetOutbox from './WorkoutSetOutbox';
import { getWorkoutSetScope, workoutSetScopeKey } from './workoutSetScope';
import WorkoutSetReads from './WorkoutSetReads';
import { publishAiBudgetRequiredFromError, publishAiBudgetRequiredFromFetch } from './aiBudgetStatus';
import { notifyAppliedMediaRequirementUpdate } from './mediaRequirementPlanUpdates';
import { getFoodInferenceImageByteBudget, prepareFoodInferenceImage } from '../utils/imageUtils';

export const FOOD_UPLOAD_TIMEOUT_MS = 3 * 60 * 1000;
export const FOOD_ANALYSIS_TIMEOUT_MS = 40000;
export const FOOD_SEARCH_TIMEOUT_MS = 15000;

// Axios's transport timeout does not include async auth interceptors. Bound the
// entire food-list operation so a stalled token lookup also reaches Retry.
const fetchFoodList = async (client, url, params) => {
  const controller = new AbortController();
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error('Food search timed out'));
      // Also prevent a late auth lookup from dispatching an obsolete request.
      controller.abort();
    }, FOOD_SEARCH_TIMEOUT_MS);
  });
  try {
    const response = await Promise.race([
      client.get(url, { params, withCredentials: true, signal: controller.signal }),
      deadline
    ]);
    return response.data;
  } finally {
    clearTimeout(timer);
  }
};

class BackendService {
  constructor(onLoadingStart, onLoadingEnd, getToken) {
    this.onLoadingStart = onLoadingStart;
    this.onLoadingEnd = onLoadingEnd;
    this.getToken = getToken; // Clerk's getToken function
    this.user = null;
    this.authenticatedIdentity = null;
    this.backgroundIdentityGeneration = 0;
    this.positionRuntime = null;
    this.positionOwner = null;
    this.userProfileRequest = null;
    this.latestUserProfileRead = null;
    this.userProfileRequestVersion = 0;
    // Local consent writes fence profile reads begun before (or during) a write.
    // This cannot make a remote-device revocation atomic with an OS GPS request.
    this.locationConsentEpoch = 0;
    this.locationConsentWrites = 0;
    this.locationConsentListeners = new Set();
    this.accountScopeListeners = new Set();
    this.appConfig = null;
    // Auth and event scope belong to this service, never to unrelated callers
    // or retained services (including the null-owner video provider).
    this.client = axios.create();
    this.setupAxiosInterceptor();
    this.imageCache = new Map();
    this.bodyweightCache = new Map();
    this.workoutSetReads = new WorkoutSetReads();
    this.workoutSetWrites = new Map();
    this.workoutExerciseOrders = { entries: new Map(), reads: new Set(), version: 0 };
    this.workoutSetOutbox = new WorkoutSetOutbox(
      (setId, updates, meta) => this.performWorkoutSetUpdate(setId, updates, meta)
    );
  }

  getProgressImage(userId, options = {}) {
    return requestProgressImage(this, userId, options);
  }

  uploadProgressImage(userId, image, options = {}) {
    return requestProgressImage(this, userId, { ...options, method: 'POST', image });
  }

  removeProgressImage(userId, options = {}) {
    return requestProgressImage(this, userId, { ...options, method: 'DELETE' });
  }

  fetchProgressImage(userId, imageUrl, options = {}) {
    return requestProgressImage(this, userId, { ...options, imageUrl });
  }

  getEventPosition = () => this.positionRuntime?.snapshot(this.positionOwner) || null;

  // Filing has a tighter freshness budget than the memory-only event ledger.
  // Snapshot once at the initiating tap; never reacquire after preflight awaits.
  getCachedCompletionPosition = () => {
    const snapshot = getCachedCompletionPosition(this);
    return snapshot && Date.now() - Date.parse(snapshot.captured_at) <= 30000 ? snapshot : null;
  };

  fetchApi = (url, options = {}) => {
    const api = new URL(API_BASE_URL || window.location.origin);
    const target = new URL(url, api);
    const headers = { ...options.headers };
    const snapshot = headers['X-Katafit-Event-Position'];
    const owner = headers['X-Katafit-Event-Owner'];
    Object.keys(headers).filter(key => key.toLowerCase() === 'x-katafit-event-owner').forEach(key => delete headers[key]);
    const ownWrite = target.origin === api.origin && target.pathname.startsWith('/api/') && ['POST', 'PUT', 'PATCH', 'DELETE'].includes((options.method || 'GET').toUpperCase());
    if (ownWrite && headers.Authorization && owner) headers['X-Katafit-Event-Owner'] = owner;
    Object.keys(headers).filter(key => key.toLowerCase() === 'x-katafit-event-position').forEach(key => delete headers[key]);
    if (target.origin === api.origin && target.pathname.startsWith('/api/') &&
        ['POST', 'PUT', 'PATCH', 'DELETE'].includes((options.method || 'GET').toUpperCase()) && headers.Authorization && snapshot) {
      headers['X-Katafit-Event-Position'] = snapshot;
    }
    // Headers were authorized by the caller; only the allowlisted transport span is measured.
    const performanceState = beginRequestPerformance({ url, method: options.method }, 'response_headers');
    if (!performanceState) return fetch(url, { ...options, headers });
    endRequestAuth(performanceState);
    return fetch(url, { ...options, headers }).then(response => {
      finishRequestPerformance(performanceState, response);
      return response;
    }, error => {
      finishRequestPerformance(performanceState, null, error);
      throw error;
    });
  };

  // Get authorization header with Clerk token
  getAuthHeaders = async (tokenOptions = {}) => {
    const position = this.getEventPosition();
    const identity = this.authenticatedIdentity;
    const owner = this.positionOwner;
    if (!this.getToken) return {};
    try {
      const token = await this.getToken(tokenOptions);
      if (identity !== this.authenticatedIdentity) throw Object.assign(new Error('Account changed before request dispatch. Please retry.'), { code: 'REQUEST_ACCOUNT_CHANGED' });
      if (token) {
        return { Authorization: `Bearer ${token}`, ...(owner ? { 'X-Katafit-Event-Owner': owner } : {}), ...(position ? { 'X-Katafit-Event-Position': JSON.stringify(position) } : {}) };
      }
    } catch (error) {
      if (error.code === 'REQUEST_ACCOUNT_CHANGED') throw error;
      console.error('Error getting Clerk token');
    }
    return {};
  };

  // A dedicated fetch avoids global Axios auth interceptors and bounds token
  // lookup as well as transport. expected owner is a fence, not authority.
  captureAppLifecycle = async (owner, events, signal) => {
    const controller = new AbortController();
    let timer, abort;
    const deadline = new Promise((_, reject) => {
      abort = () => { controller.abort(); reject(new Error('Lifecycle request cancelled')); };
      if (signal.aborted) abort();
      else signal.addEventListener('abort', abort, { once: true });
      timer = setTimeout(abort, 10000);
    });
    try {
      return await Promise.race([deadline, (async () => {
        const token = await this.getToken?.({ skipCache: true });
        if (signal.aborted || controller.signal.aborted || !token) throw new Error('Lifecycle authentication unavailable');
        const response = await this.fetchApi(`${API_BASE_URL}/api/user/activity-events/lifecycle`, {
          method: 'POST', signal: controller.signal, credentials: 'omit',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ expected_owner_user_id: owner, events }),
        });
        const body = await response.json();
        if (!response.ok) throw Object.assign(new Error('Lifecycle capture rejected'), { status: response.status, code: body.code });
        return body;
      })()]);
    } finally { clearTimeout(timer); signal.removeEventListener('abort', abort); }
  };

  // App Configuration
  fetchAppConfig = async () => {
    try {
      const response = await this.fetchApi(`${API_BASE_URL}/api/config`);
      if (response.ok) {
        this.appConfig = await response.json();
        return this.appConfig;
      }
    } catch (error) {
      console.error('Error fetching app config:', error);
    }
    // Default fallback
    this.appConfig = { appName: 'Kata.fit', version: '1.0.0' };
    return this.appConfig;
  };

  getAppName = () => {
    return this.appConfig?.appName || 'Kata.fit';
  };

  // Authentication
  setupAxiosInterceptor = () => {
    // Add request interceptor to attach Clerk token
    this.client.interceptors.request.use(
      async (config) => {
        const performanceState = beginRequestPerformance(config, 'response_decoded');
        config.katafitRequestPerformance = performanceState;
        try {
          // Only add auth header for our API. Callers that explicitly requested a
          // fresh token (for long-lived user actions) must not be overwritten by
          // Clerk's default cached-token lookup here.
          const headers = config.headers;
          const hasAuthorization = typeof headers?.has === 'function'
            ? headers.has('Authorization')
            : Object.keys(headers || {}).some(name => name.toLowerCase() === 'authorization');
          const identity = Object.prototype.hasOwnProperty.call(config, 'katafitEventIdentity') ? config.katafitEventIdentity : this.authenticatedIdentity;
          const owner = Object.prototype.hasOwnProperty.call(config, 'katafitEventOwner') ? config.katafitEventOwner : this.positionOwner;
          let snapshot = Object.prototype.hasOwnProperty.call(config, 'katafitEventPosition') ? config.katafitEventPosition : this.getEventPosition();
          const api = new URL(API_BASE_URL || window.location.origin);
          const target = new URL(config.url, api);
          const ownApi = target.origin === api.origin && target.pathname.startsWith('/api/');
          const write = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase());
          // Explicitly owner-bound reads share the write auth boundary. Generic
          // GETs without an initiating owner keep their existing semantics.
          const ownerBound = write || Object.prototype.hasOwnProperty.call(config, 'katafitEventOwner');
          const assertScope = () => {
            config.katafitBackgroundScope?.();
            if (ownApi && write) config.katafitCompletionIntent?.assertCurrent();
            if (ownApi && (identity !== this.authenticatedIdentity || (ownerBound && owner !== this.positionOwner))) {
              throw Object.assign(new Error('Account changed before request dispatch. Please retry.'), { code: 'REQUEST_ACCOUNT_CHANGED' });
            }
            if (ownApi && write && config.katafitOptionalStatusPosition &&
                (config.katafitLocationConsentEpoch !== this.getLocationConsentEpoch() || this.user?.location_capture_enabled !== true ||
                 !getCachedCompletionPosition({ getEventPosition: () => snapshot }))) {
              snapshot = null;
              config.katafitEventPosition = null;
              // Status payload is an object before Axios request transforms.
              if (config.data && typeof config.data === 'object') delete config.data.position;
            }
          };
          assertScope();
          if (ownApi && write) {
            // Preserve the initiating scope across asynchronous interceptor work,
            // independently of whether a GPS snapshot exists.
            config.katafitEventIdentity = identity;
            config.katafitEventOwner = owner;
          }
          if (ownApi && write && this.positionOwner) {
            if (snapshot && snapshot.owner_user_id !== this.positionOwner) throw new Error('Event position belongs to another account. Please retry under its owner.');
            // Preserve even null across asynchronous auth lookup.
            config.katafitEventPosition = snapshot;
          }
          if (ownApi && !hasAuthorization) {
            const authHeaders = await this.getAuthHeaders();
            config.headers = { ...config.headers, ...authHeaders };
          }
          assertScope();
          const authorization = typeof config.headers?.get === 'function' ? config.headers.get('Authorization')
            : Object.entries(config.headers || {}).find(([key]) => key.toLowerCase() === 'authorization')?.[1];
          if (ownApi && write && config.katafitCompletionIntent && !authorization) {
            throw new Error('Authentication unavailable. Your activity remains pending.');
          }
          if (ownApi && config.katafitOptionalStatusPosition && !authorization) {
            throw Object.assign(new Error('Authentication unavailable. Please retry your check-in.'), { code: 'STATUS_AUTH_REQUIRED' });
          }
          if (ownApi && write && owner && authorization) config.headers['X-Katafit-Event-Owner'] = owner;
          if (!ownApi || !write) {
            if (typeof config.headers?.delete === 'function') config.headers.delete('X-Katafit-Event-Owner');
            else Object.keys(config.headers || {}).filter(key => key.toLowerCase() === 'x-katafit-event-owner').forEach(key => delete config.headers[key]);
          }
          if (ownApi && write && snapshot && this.positionOwner === snapshot.owner_user_id && authorization) {
            config.headers['X-Katafit-Event-Position'] = JSON.stringify(snapshot);
          }
          if (!ownApi || !write || !snapshot) {
            if (typeof config.headers?.delete === 'function') config.headers.delete('X-Katafit-Event-Position');
            else Object.keys(config.headers || {}).filter(key => key.toLowerCase() === 'x-katafit-event-position').forEach(key => delete config.headers[key]);
          }
          endRequestAuth(performanceState);
          return config;
        } catch (error) {
          finishRequestPerformance(performanceState, null, error);
          throw error;
        }
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      response => {
        finishRequestPerformance(response.config?.katafitRequestPerformance, response);
        return response;
      },
      error => {
        finishRequestPerformance(error.config?.katafitRequestPerformance, error.response, error);
        // Axios errors retain request configs; never expose event GPS in UI logs.
        for (const config of [error.config, error.response?.config]) {
          if (!config) continue;
          if (typeof config.headers?.delete === 'function') config.headers.delete('X-Katafit-Event-Position');
          else Object.keys(config.headers || {}).filter(key => key.toLowerCase() === 'x-katafit-event-position').forEach(key => delete config.headers[key]);
          delete config.katafitEventPosition;
          // Filing/check-in bodies may also carry a fix. Keep the original
          // request private instead of leaving it on a console-printable error.
          if (config.url?.endsWith('/position') || config.url?.endsWith('/status')) delete config.data;
        }
        if (error.response && error.response.status === 401) {
          const isBackendRequest = error.response.config.url.startsWith(API_BASE_URL);
          if (isBackendRequest) {
            // Token might be expired, Clerk will handle re-auth
            console.warn('Authentication error, Clerk will handle re-auth');
          }
        }
        publishAiBudgetRequiredFromError(error);
        return Promise.reject(error);
      }
    );
  };

  onAccountScopeChanged = (listener) => {
    this.accountScopeListeners.add(listener);
    return () => this.accountScopeListeners.delete(listener);
  };

  notifyAccountScopeChanged = () => {
    resetCoachSequenceForIdentity(this.authenticatedIdentity);
    this.accountScopeListeners.forEach(listener => {
      try { listener(); } catch (_) { /* Observers cannot interrupt logout/account changes. */ }
    });
  };

  setAuthenticatedIdentity = (identity) => {
    const nextIdentity = identity == null ? null : String(identity);
    if (nextIdentity === this.authenticatedIdentity) return;

    this.positionRuntime?.dispose();
    this.positionRuntime = null; this.positionOwner = null;
    this.workoutSetOutbox?.clearEventPositions();
    this.workoutSetReads = new WorkoutSetReads();
    this.statusRetryIntents?.clear();
    this.authenticatedIdentity = nextIdentity;
    this.backgroundIdentityGeneration += 1;
    this.user = null;
    this.userProfileRequest = null;
    this.latestUserProfileRead = null;
    // Invalidate every response that began under the previous principal.
    this.userProfileRequestVersion += 1;
    this.locationConsentEpoch += 1;
    this.notifyAccountScopeChanged();
  };

  clearAuthenticatedIdentity = () => {
    this.positionRuntime?.dispose();
    this.positionRuntime = null; this.positionOwner = null;
    this.workoutSetOutbox?.clearEventPositions();
    this.workoutSetReads = new WorkoutSetReads();
    this.statusRetryIntents?.clear();
    this.authenticatedIdentity = null;
    this.backgroundIdentityGeneration += 1;
    this.user = null;
    this.userProfileRequest = null;
    this.latestUserProfileRead = null;
    this.userProfileRequestVersion += 1;
    this.locationConsentEpoch += 1;
    this.notifyAccountScopeChanged();
  };

  // Sync Clerk user with backend (creates/updates user in our database)
  syncClerkUser = async (clerkUser) => {
    const identity = clerkUser?.id == null ? null : String(clerkUser.id);
    this.setAuthenticatedIdentity(identity);
    const performanceState = beginRequestPerformance({ url: `${API_BASE_URL}/api/auth/clerk/sync`, method: 'post' }, 'after_body_handling');
    let performanceResponse;
    let performanceError;
    try {
      const authHeaders = await this.getAuthHeaders({ skipCache: true });
      endRequestAuth(performanceState);
      if (identity !== this.authenticatedIdentity) return null;
      if (!authHeaders.Authorization) throw new Error('Current Clerk session is unavailable');
      const response = await this.fetchApi(`${API_BASE_URL}/api/auth/clerk/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          clerk_user_id: identity
        })
      });
      performanceResponse = response;
      if (response.ok) {
        const userData = await response.json();
        if (identity !== this.authenticatedIdentity) return null;
        // The endpoint replies with { user, isNewUser }; every consumer of
        // getUser() expects the flat user document.
        this.user = userData?.user || userData;
        return this.user;
      } else {
        throw new Error('Failed to sync user with backend');
      }
    } catch (error) {
      performanceError = error;
      if (identity !== this.authenticatedIdentity) return null;
      throw error;
    } finally {
      finishRequestPerformance(performanceState, performanceResponse, performanceError);
    }
  };

  getUser = () => {
    return this.user;
  };

  // Datetime manipulation
  formatDateUTC(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateTimeUTC(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  }

  // User Profile
  async fetchUserProfile({ shareInFlight = false, requireAuthoritative = false } = {}) {
    const identity = this.authenticatedIdentity;
    if (shareInFlight && !requireAuthoritative && this.userProfileRequest?.identity === identity) {
      return this.userProfileRequest.promise;
    }
    const epoch = this.getLocationConsentEpoch();
    const unavailable = () => Object.assign(
      new Error('Could not verify current profile settings. Please try again; your activity remains pending.'),
      { code: 'PROFILE_READ_UNAVAILABLE' }
    );
    const consentChanged = () => Object.assign(
      new Error('Position consent changed during submission. Your activity remains pending.'),
      { code: 'PROFILE_CONSENT_CHANGED' }
    );
    if (requireAuthoritative && epoch === null) throw consentChanged();

    this.userProfileRequest = null;
    this.latestUserProfileRead = null;
    const controller = requireAuthoritative ? new AbortController() : null;
    const entry = { identity, epoch, version: ++this.userProfileRequestVersion, accepted: false };
    const request = (async () => {
      const performanceState = beginRequestPerformance({ url: `${API_BASE_URL}/api/user`, method: 'get' }, 'after_body_handling');
      let performanceResponse;
      let performanceError;
      try {
        const headers = await this.getAuthHeaders();
        endRequestAuth(performanceState);
        // Token lookup can outlive logout, an A→B→A switch or a consent write.
        if (identity !== this.authenticatedIdentity) return null;
        if (epoch !== this.getLocationConsentEpoch() || controller?.signal.aborted) {
          if (requireAuthoritative) throw consentChanged();
          return null;
        }
        const response = await this.fetchApi(`${API_BASE_URL}/api/user`, {
          headers, cache: 'no-store', ...(controller ? { signal: controller.signal } : {}),
        });
        performanceResponse = response;
        if (!response.ok) throw new Error('Failed to fetch user profile');
        const profile = await response.json();
        if (identity !== this.authenticatedIdentity) return null;
        // Ordinary refresh order controls cache publication, NOT consent scope.
        // A real authority change must never publish a pre-change response.
        entry.accepted = epoch !== null && epoch === this.getLocationConsentEpoch() &&
          !controller?.signal.aborted && entry.version === this.userProfileRequestVersion;
        if (entry.accepted) {
          const previousCapture = this.user?.location_capture_enabled;
          this.user = profile;
          if (profile.location_capture_enabled !== true) this.positionRuntime?.configure?.(this.positionOwner, false);
          if (previousCapture !== undefined && previousCapture !== profile.location_capture_enabled) {
            this.locationConsentEpoch += 1;
            // Record the response's own transition before synchronous listeners.
            entry.publishedEpoch = this.getLocationConsentEpoch();
            this.locationConsentListeners.forEach(listener => {
              try { listener(); } catch (_) { /* Profile authority still updates on listener failure. */ }
            });
          } else entry.publishedEpoch = this.getLocationConsentEpoch();
        }
        return profile;
      } catch (error) {
        performanceError = error;
        if (identity !== this.authenticatedIdentity) return null;
        throw error;
      } finally {
        finishRequestPerformance(performanceState, performanceResponse, performanceError);
      }
    })();
    entry.promise = request;
    // Retain only the most recently STARTED actual read, including its result.
    // This is not a profile cache: an authority caller always starts its own
    // no-store GET and can only converge onto a read begun at/after that GET.
    this.latestUserProfileRead = entry;

    if (requireAuthoritative) {
      let timer;
      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => { controller.abort(); reject(unavailable()); }, 15000);
      });
      const converge = async () => {
        let current = entry;
        for (let joins = 0; joins <= 8; joins++) {
          const profile = await Promise.race([current.promise, deadline]);
          if (identity !== this.authenticatedIdentity) return null;
          if (current.epoch !== epoch || epoch === null) throw consentChanged();
          if (current.accepted && current.publishedEpoch === this.getLocationConsentEpoch()) return profile;
          if (epoch !== this.getLocationConsentEpoch()) throw consentChanged();
          const latest = this.latestUserProfileRead;
          if (!latest || latest === current || latest.identity !== identity || latest.epoch !== epoch) throw unavailable();
          current = latest;
        }
        throw unavailable();
      };
      try { return await Promise.race([converge(), deadline]); }
      finally { clearTimeout(timer); }
    }

    if (!shareInFlight) return request;
    const sharedEntry = { identity, promise: request };
    this.userProfileRequest = sharedEntry;
    try { return await request; }
    finally { if (this.userProfileRequest === sharedEntry) this.userProfileRequest = null; }
  }

  // Consent reads must be fresh, and must not overlap a local consent write.
  getLocationConsentEpoch = () => this.locationConsentWrites ? null : this.locationConsentEpoch;
  onLocationConsentChanged = (listener) => {
    this.locationConsentListeners.add(listener);
    return () => this.locationConsentListeners.delete(listener);
  };

  beginLocationConsentWrite = () => {
    this.locationConsentEpoch += 1;
    this.locationConsentWrites += 1;
    this.userProfileRequest = null;
    this.latestUserProfileRead = null;
    this.userProfileRequestVersion += 1;
    this.locationConsentListeners.forEach(listener => {
      try { listener(); } catch (_) { /* A listener cannot interrupt consent persistence. */ }
    });
  };

  endLocationConsentWrite = () => {
    this.locationConsentWrites -= 1;
    this.locationConsentEpoch += 1;
    this.userProfileRequest = null;
    this.latestUserProfileRead = null;
    this.userProfileRequestVersion += 1;
    this.locationConsentListeners.forEach(listener => {
      try { listener(); } catch (error) { console.error('Location consent listener failed:', error); }
    });
  };

  // Independent trail plane: no foreground fix, profile cache or persisted JS data.
  async backgroundPositionRequest(method, path, { data, params, signal } = {}) {
    const owner = this.positionOwner, identity = this.authenticatedIdentity, generation = this.backgroundIdentityGeneration;
    const current = () => {
      if (!owner || !identity || owner !== this.positionOwner || identity !== this.authenticatedIdentity || generation !== this.backgroundIdentityGeneration) {
        throw Object.assign(new Error('Background position account changed. Reopen this view.'), { code: 'REQUEST_ACCOUNT_CHANGED' });
      }
    };
    current();
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort(); else signal?.addEventListener('abort', abort, { once: true });
    let timer;
    const deadline = new Promise((_, reject) => { timer = setTimeout(() => {
      controller.abort(); reject(new Error('Background position request timed out. Retry explicitly.'));
    }, 15000); });
    try {
      const response = await Promise.race([deadline, this.client.request({
        method, url: `${API_BASE_URL}/api/${path}`, data, params, signal: controller.signal,
        withCredentials: true, headers: { 'Cache-Control': 'no-store' },
        katafitEventIdentity: identity, katafitEventOwner: owner, katafitEventPosition: null, katafitBackgroundScope: current
      })]);
      current();
      if (controller.signal.aborted) throw new Error('Background position request cancelled');
      return response.data;
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
  }

  getBackgroundLocationCapture(options) {
    return this.backgroundPositionRequest('get', 'user/background-location-capture', options);
  }
  updateBackgroundLocationCapture(enabled, revision, options = {}) {
    return this.backgroundPositionRequest('put', 'user/background-location-capture', { ...options, data: {
      background_location_capture_enabled: enabled,
      ...(enabled ? { expected_revision: revision, disclosure_version: 'bgpos-disclosure-2026-10-07' } : {})
    } });
  }
  async updateBackgroundPositionAudience(audience, options = {}) {
    if (!Array.isArray(audience) || !(audience.length === 0 || (audience.length === 1 && audience[0] === 'dojo_chief'))) {
      throw new Error('Background trails support Private or Dojo Chief only');
    }
    return this.backgroundPositionRequest('put', 'users/me/privacy-settings', { ...options, data: {
      background_position: audience,
      ...(audience.length ? { background_disclosure_version: 'bgpos-disclosure-2026-10-07' } : {})
    } });
  }
  enrollBackgroundPositionDevice(data, options = {}) {
    return this.backgroundPositionRequest('post', 'background-position/devices', { ...options, data });
  }
  getBackgroundPositionDevices(options) {
    return this.backgroundPositionRequest('get', 'background-position/devices', options);
  }
  revokeBackgroundPositionDevice(deviceId, options) {
    return this.backgroundPositionRequest('delete', `background-position/devices/${encodeURIComponent(deviceId)}`, options);
  }
  async getBackgroundPositionHistory(window, memberId = null, options = {}) {
    const { from, to, limit = 200, cursor } = window;
    const utc = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T.*Z$/.test(value) && Number.isFinite(Date.parse(value));
    if (!utc(from) || !utc(to) || Date.parse(to) <= Date.parse(from) || Date.parse(to) - Date.parse(from) > 31 * 86400000 ||
      !Number.isInteger(limit) || limit < 1 || limit > 500) throw new Error('Select a half-open UTC window of 1–31 days and at most 500 samples per page.');
    return this.backgroundPositionRequest('get', memberId
      ? `background-position/dojo/members/${encodeURIComponent(memberId)}/history` : 'background-position/history',
    { ...options, params: { from, to, limit, ...(cursor ? { cursor } : {}) } });
  }

  async updateLocationCapture(enabled) {
    this.beginLocationConsentWrite();
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/user/location-capture`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ location_capture_enabled: enabled })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to update location capture');
      }
      return response.json();
    } finally { this.endLocationConsentWrite(); }
  }

  async updateUserProfile(profileData) {
    this.beginLocationConsentWrite();
    let settingsWriteOutcome = 'not attempted';
    try {
      const authHeaders = await this.getAuthHeaders();
      const body = JSON.stringify(profileData);
      settingsWriteOutcome = 'uncertain';
      const response = await this.fetchApi(`${API_BASE_URL}/api/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body
      });
      if (!response.ok) {
        settingsWriteOutcome = 'failed';
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to update profile');
      }
    } catch (error) {
      // Only these settings producers expose stage metadata to the save ledger.
      error.settingsWriteOutcome = settingsWriteOutcome;
      console.error('Error updating profile:', error);
      throw error;
    } finally { this.endLocationConsentWrite(); }
  }

  // AI model selection and spend
  async fetchLlmModels() {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/models`, { headers });
    if (!response.ok) throw new Error('Failed to fetch AI model options');
    return await response.json();
  }

  // Separate from fetchLlmModels because the list is large and only needed
  // when a user actually opens per-task model settings.
  async fetchLlmModelOptions() {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/models/options`, { headers });
    if (!response.ok) throw new Error('Failed to fetch AI model list');
    const payload = await response.json();
    return payload.models || [];
  }

  async fetchLlmProviders() {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/providers`, { headers });
    if (!response.ok) throw new Error('Failed to fetch custom AI providers');
    const payload = await response.json();
    return payload.providers || [];
  }

  async saveLlmProviders(providers) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/providers`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ providers })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to save custom AI providers');
    return payload.providers || [];
  }

  async fetchMcpServers(scope) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/mcp/servers${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch custom MCP servers');
    return response.json();
  }

  async createMcpEnrollment(name, scope) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/mcp/servers/enrollment`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify({ name, ...(scope ? { scope } : {}) }) });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to create MCP enrollment');
    return payload;
  }


  async deleteMcpServer(serverId, scope) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/mcp/servers/${encodeURIComponent(serverId)}${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`, { method: 'DELETE', headers });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to remove MCP server');
    return payload;
  }


  // Providers and their models are managed one operation at a time so each
  // action persists immediately and reports its own success or failure.
  async addLlmProvider({ name, base_url, api_key }) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/providers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name, base_url, api_key })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to add the provider');
    return payload.providers || [];
  }

  async updateLlmProvider(providerId, updates) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/providers/${encodeURIComponent(providerId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(updates || {})
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to update the provider');
    return payload.providers || [];
  }

  async deleteLlmProvider(providerId) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/providers/${encodeURIComponent(providerId)}`, {
      method: 'DELETE',
      headers: authHeaders
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to remove the provider');
    return payload.providers || [];
  }

  async addLlmProviderModel(providerId, model) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/providers/${encodeURIComponent(providerId)}/models`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(model || {})
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to add the model');
    return payload.providers || [];
  }

  async updateLlmProviderModel(providerId, modelId, model) {
    const authHeaders = await this.getAuthHeaders();
    const modelPath = modelId.split('/').map(encodeURIComponent).join('/');
    const response = await this.fetchApi(
      `${API_BASE_URL}/api/llm/providers/${encodeURIComponent(providerId)}/models/${modelPath}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(model || {})
      }
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to update the model');
    return payload.providers || [];
  }

  async deleteLlmProviderModel(providerId, modelId) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(
      `${API_BASE_URL}/api/llm/providers/${encodeURIComponent(providerId)}/models/${modelId.split('/').map(encodeURIComponent).join('/')}`,
      { method: 'DELETE', headers: authHeaders }
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Failed to remove the model');
    return payload.providers || [];
  }

  async testLlmProvider(provider, model_id) {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/providers/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ provider, model_id })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.ok !== true) {
      const message = [payload.error || 'Provider test failed', payload.next_step,
        payload.reference ? `Reference: ${payload.reference}` : null].filter(Boolean).join(' ');
      throw Object.assign(new Error(message), { code: payload.code });
    }
    return payload;
  }

  async updateLlmSettings(settings) {
    let settingsWriteOutcome = 'not attempted';
    try {
      const authHeaders = await this.getAuthHeaders();
      const body = JSON.stringify(settings);
      settingsWriteOutcome = 'uncertain';
      const response = await this.fetchApi(`${API_BASE_URL}/api/llm/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body
      });
      if (!response.ok) {
        settingsWriteOutcome = 'failed';
        const payload = await response.json().catch(() => ({}));
        // Preserve confirmed capability rejection, even if its error body is unreadable.
        throw new Error(payload.error || 'Failed to save AI model settings');
      }
      return await response.json();
    } catch (error) {
      error.settingsWriteOutcome = settingsWriteOutcome;
      throw error;
    }
  }

  async fetchLlmUsage(days = 30) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/usage?days=${encodeURIComponent(days)}`, { headers });
    if (!response.ok) throw new Error('Failed to fetch AI usage');
    return await response.json();
  }

  async fetchLlmBalance() {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/llm/balance`, { headers });
    if (!response.ok) throw new Error('Failed to fetch AI balance');
    return await response.json();
  }

  async uploadProfilePicture(formData) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/user/avatar`, {
        method: 'POST',
        headers,
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.error || 'Failed to upload avatar';
        throw new Error(message);
      }
      // keep cached user in sync if present
      if (data?.avatar_url) {
        this.user = {
          ...(this.user || {}),
          avatar_url: data.avatar_url,
        };
        if (typeof this.onAuthChange === 'function' && this.isAuthenticated !== null) {
          this.onAuthChange(this.isAuthenticated, this.user);
        }
      }
      return data;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    }
  }

  /**
   * Reset avatar to use the picture from the user's sign-in account (Google, Facebook, etc.)
   */
  async useAccountPicture() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/user/avatar/use-account-picture`, {
        method: 'POST',
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.error || 'Failed to reset avatar';
        throw new Error(message);
      }
      // keep cached user in sync if present
      if (data?.avatar_url) {
        this.user = {
          ...(this.user || {}),
          avatar_url: data.avatar_url,
          clerk_avatar_url: data.clerk_avatar_url,
        };
        if (typeof this.onAuthChange === 'function' && this.isAuthenticated !== null) {
          this.onAuthChange(this.isAuthenticated, this.user);
        }
      }
      return data;
    } catch (error) {
      console.error('Error resetting to account picture:', error);
      throw error;
    }
  }

  async fetchRecommendations() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/recommendations/latest`, {
        method: 'GET',
        headers
      });
      if (response.status === 204) {
          return null; // Nothing new
      }
      if (response.status === 202) {
          return await response.json();
      }
      if (response.ok) {
        return await response.json();
      } else {
        console.error('Failed to fetch recommendations', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return null;
    }
  }

  async forceRegenerateRecommendation() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/recommendations/regenerate`, {
        method: 'POST',
        headers
      });
      await publishAiBudgetRequiredFromFetch(response);
      if (response.ok) {
        const recommendation = await response.json();
        notifyAppliedMediaRequirementUpdate(recommendation);
        return recommendation;
      } else {
        console.error('Failed to regenerate recommendation', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error regenerating recommendation:', error);
      return null;
    }
  }

  async markRecommendationSeen(id) {
      try {
          const headers = await this.getAuthHeaders();
          await this.fetchApi(`${API_BASE_URL}/api/recommendations/${id}/seen`, {
              method: 'POST',
              headers
          });
      } catch (e) {
          console.error(e);
      }
  }

  /**
   * Fetch recommendation for a specific date
   * @param {string} dateStr - Date in YYYY-MM-DD format
   * @returns {Object|null} - The recommendation or null
   */
  async fetchRecommendationByDate(dateStr) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/recommendations/by-date/${dateStr}`, {
        method: 'GET',
        headers
      });
      if (response.status === 204) {
        return null;
      }
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching recommendation by date:', error);
      return null;
    }
  }

  /**
   * Fetch recommendations for multiple dates (batch)
   * @param {string[]} dates - Array of dates in YYYY-MM-DD format
   * @returns {Object} - Map of date -> recommendation
   */
  async fetchRecommendationsByDates(dates) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/recommendations/by-dates`,
        { dates },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching recommendations by dates:', error);
      return {};
    }
  }

  async fetchUserAffiliation() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/user/affiliation`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user affiliation:', error);
      throw error;
    }
  }

  async fetchActivityStats(startDate, endDate) {
    startDate = this.formatDateUTC(startDate);
    endDate = this.formatDateUTC(endDate);
    try {
      const queryParams = {
        granularity: 'weekly',
        start_date: startDate,
        end_date: endDate
      };
      const response = await this.client.get(`${API_BASE_URL}/api/user/activity_stats`, {
        params: queryParams,
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching workout stats:', error);
      throw error;
    }
  }

  // Fetch compliance for given days array or single day value.
  // If `days` is an array, the API returns an object mapping days to results.
  // If `days` is a single number, the API returns a single result object.
  async fetchCompliance(days) {
    try {
      const params = { _ts: Date.now() };
      if (Array.isArray(days)) {
        params.days = days.join(',');
      } else if (typeof days === 'number') {
        params.days = String(days);
      } else {
        throw new Error('days must be a number or array of numbers');
      }
      const response = await this.client.get(`${API_BASE_URL}/api/user/compliance`, {
        params,
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance:', error);
      throw error;
    }
  }

  // Fetch badge stats (volume PRs and nutrition compliance) in one call.
  // Backend may respond with 202 + { computing: true } when no cache exists
  // and a heavy recompute is in flight. Poll until the data is ready.
  async fetchBadgeStats() {
    const MAX_ATTEMPTS = 30; // ~60s total
    const POLL_DELAY_MS = 2000;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      try {
        const response = await this.client.get(`${API_BASE_URL}/api/user/badge_stats`, {
          withCredentials: true,
          validateStatus: (status) => status === 200 || status === 202,
        });
        if (response.status === 202) {
          await new Promise(r => setTimeout(r, POLL_DELAY_MS));
          continue;
        }
        return response.data;
      } catch (error) {
        console.error('Error fetching badge stats:', error);
        throw error;
      }
    }
    throw new Error('Badge stats still computing after maximum polling attempts');
  }

  // Workouts
  async createAiSupervisedWorkout(circuit) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/workouts/ai-supervised`, circuit, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating AI-supervised workout:', error);
      throw error;
    }
  }

  async previewTravelReplacement(workoutId) {
    const response = await this.client.get(
      `${API_BASE_URL}/api/workouts/${workoutId}/travel-replacement`,
      { withCredentials: true }
    );
    return response.data;
  }

  async createTravelReplacement(workoutId, options) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/workouts/${workoutId}/travel-replacement`,
      options,
      { withCredentials: true }
    );
    return response.data;
  }

  async attachExistingTravelReplacement(workoutId, replacementActivityId) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/workouts/${workoutId}/travel-replacement/existing`,
      { replacement_activity_id: replacementActivityId },
      { withCredentials: true }
    );
    return response.data;
  }

  async cancelTravelReplacement(replacementId) {
    const response = await this.client.delete(
      `${API_BASE_URL}/api/workouts/travel-replacements/${replacementId}`,
      { withCredentials: true }
    );
    return response.data;
  }

  async getTravelReplacementAllowance() {
    const response = await this.client.get(
      `${API_BASE_URL}/api/users/me/travel-replacements`,
      { withCredentials: true }
    );
    return response.data;
  }

  async updateTravelReplacementAllowance(perYear) {
    const response = await this.client.put(
      `${API_BASE_URL}/api/users/me/travel-replacements`,
      { per_year: perYear },
      { withCredentials: true }
    );
    return response.data;
  }

  async updateWorkout(workoutId, updates) {
    try {
      const response = await this.client.put(`${API_BASE_URL}/api/workouts/${workoutId}`, updates, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error updating workout:', error);
      throw error;
    }
  }

  workoutExerciseOrderKey(workoutId) {
    return JSON.stringify([this.backgroundIdentityGeneration, this.user?._id, String(workoutId)]);
  }

  pruneWorkoutExerciseOrders() {
    const state = this.workoutExerciseOrders;
    for (const [key, entry] of state.entries) {
      if (!entry.pending && (Date.now() - entry.savedAt > 60000
        || (entry.confirmed != null && ![...state.reads].some(read => read.key === key && read.version <= entry.confirmed)))) {
        state.entries.delete(key);
      }
    }
  }

  reconcileWorkoutExerciseOrder(workoutId, activity, read) {
    const state = this.workoutExerciseOrders;
    const entry = state.entries.get(this.workoutExerciseOrderKey(workoutId));
    const rows = activity?.data?.exercises;
    if (!entry || !Array.isArray(rows)) return activity;

    // A no-cache GET dispatched after the acknowledgement is authoritative,
    // including a different order from another device. Only overlapping reads
    // and pending writes need projection, not a permanent matching-order cache.
    if (!entry.pending && read.version >= entry.version
      && (entry.confirmed == null || read.version >= entry.confirmed)) {
      entry.confirmed = read.version;
      entry.ids = rows.map(row => String(row._id));
      return activity;
    }
    // Reorder only surviving known slots. Canonical membership, new rows and all
    // set/superset fields always come from this read, never a cached activity.
    const byId = new Map(rows.map(row => [String(row._id), row]));
    const ordered = entry.ids.filter(id => byId.has(id)).map(id => byId.get(id));
    const known = new Set(entry.ids);
    let index = 0;
    return { ...activity, data: { ...activity.data, exercises: rows.map(row => known.has(String(row._id)) ? ordered[index++] : row) } };
  }

  async reorderExercises(workoutId, exerciseIds) {
    this.pruneWorkoutExerciseOrders();
    const state = this.workoutExerciseOrders;
    const key = this.workoutExerciseOrderKey(workoutId);
    const entry = { ids: exerciseIds.map(String), pending: true, version: ++state.version };
    state.entries.set(key, entry);
    try {
      await this.queueWorkoutSetWrite({ workoutId }, async () => {
        const send = () => this.client.put(
          `${API_BASE_URL}/api/workouts/${workoutId}/reorder_exercises`, { newOrder: entry.ids },
          { withCredentials: true }
        );
        try { await send(); } catch (error) {
          // Only the untyped exact-array CAS conflict can be retried. Permission,
          // lifecycle and protection denials remain terminal, as on the server.
          if (error.response?.status !== 409 || error.response?.data?.code
            || state.entries.get(key) !== entry || key !== this.workoutExerciseOrderKey(workoutId)) throw error;
          const fresh = await this.fetchActivity(workoutId);
          const ids = fresh?.data?.exercises?.map(row => String(row._id));
          if (key !== this.workoutExerciseOrderKey(workoutId) || state.entries.get(key) !== entry
            || !['pending', 'ongoing'].includes(fresh?.status)
            || !ids || ids.length !== entry.ids.length || entry.ids.some(id => !ids.includes(id))) throw error;
          await send(); // At most one fresh backend read/CAS attempt.
        }
      });
      if (state.entries.get(key) === entry) {
        entry.pending = false;
        entry.version = ++state.version;
        entry.savedAt = Date.now();
      }
    } catch (error) {
      if (state.entries.get(key) === entry) state.entries.delete(key);
      console.error('Error reordering workout exercises:', error);
      throw error;
    }
  }

  async addExercise(workoutId, exerciseId) {
    try {
      const workoutWorkoutExerciseToSubmit = { exercise_id: exerciseId, duplicate_last_sets: true, weight: 0, repetitions: 0, complete: false };
      await this.client.post(`${API_BASE_URL}/api/workouts/${workoutId}/exercises`, workoutWorkoutExerciseToSubmit, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error adding exercise:', error);
      throw error;
    }
  }

  async removeWorkoutExercise(workoutId, workoutExerciseId) {
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/workouts/${workoutId}/exercises/${workoutExerciseId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `Failed to remove workout exercise (${response.status})`);
      }
    } catch (error) {
      console.error('Error removing workout exercise:', error);
      throw error;
    }
  }

  // =====================
  // Superset Management
  // =====================

  /**
   * Create a new superset from multiple exercises
   * @param {string} workoutId - The workout activity ID
   * @param {string[]} workoutExerciseIds - Array of workout exercise IDs to group
   * @returns {Promise<{superset_group: number}>}
   */
  async createSuperset(workoutId, workoutExerciseIds) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/workouts/${workoutId}/supersets`,
        { workout_exercise_ids: workoutExerciseIds },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating superset:', error);
      throw error;
    }
  }

  /**
   * Add an exercise to an existing superset
   * @param {string} workoutId - The workout activity ID
   * @param {number} supersetGroup - The superset group number
   * @param {string} workoutExerciseId - The workout exercise ID to add
   */
  async addToSuperset(workoutId, supersetGroup, workoutExerciseId) {
    try {
      await this.client.post(
        `${API_BASE_URL}/api/workouts/${workoutId}/supersets/${supersetGroup}/exercises`,
        { workout_exercise_id: workoutExerciseId },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error adding to superset:', error);
      throw error;
    }
  }

  /**
   * Remove an exercise from its superset
   * @param {string} workoutId - The workout activity ID
   * @param {string} workoutExerciseId - The workout exercise ID to remove from superset
   */
  async removeFromSuperset(workoutId, workoutExerciseId) {
    try {
      await this.client.delete(
        `${API_BASE_URL}/api/workouts/${workoutId}/exercises/${workoutExerciseId}/superset`,
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error removing from superset:', error);
      throw error;
    }
  }

  /**
   * Dissolve an entire superset
   * @param {string} workoutId - The workout activity ID
   * @param {number} supersetGroup - The superset group number to dissolve
   */
  async dissolveSuperset(workoutId, supersetGroup) {
    try {
      await this.client.delete(
        `${API_BASE_URL}/api/workouts/${workoutId}/supersets/${supersetGroup}`,
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error dissolving superset:', error);
      throw error;
    }
  }

  async startNewWorkout(previousWorkoutId, workoutName = null, location = null) {
    try {
      const payload = { previous_workout_id: previousWorkoutId };
      if(workoutName) {
        payload.workout_name = workoutName;
      }
      if (location && location.latitude !== undefined && location.longitude !== undefined) {
        payload.latitude = location.latitude;
        payload.longitude = location.longitude;
      }
      const response = await this.client.post(`${API_BASE_URL}/api/workouts`, payload, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error starting new workout:', error);
      throw error;
    }
  }

  async fetchWorkoutSets(workoutId, workoutExerciseId) {
    const reads = this.workoutSetReads;
    const started = reads.begin();
    const generation = this.backgroundIdentityGeneration;
    const owner = this.user?._id;
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/workouts/${workoutId}/exercises/${workoutExerciseId}/sets`, {
        withCredentials: true
      });
      if (generation !== this.backgroundIdentityGeneration || owner !== this.user?._id) {
        throw new Error('Workout set read scope changed.');
      }
      const context = { workoutId, workoutExerciseId, workoutSetIdentity: this.authenticatedIdentity, workoutSetOwner: this.user?._id };
      return reads.reconcile(response.data, `${workoutId}:${workoutExerciseId}:`, started,
        setId => this.workoutSetOutbox.getPendingUpdates(setId, context));
    } catch (error) {
      console.error('Error fetching workout sets for workout exercise with id:', workoutId, workoutExerciseId, error);
      throw error;
    } finally {
      reads.end();
    }
  }

  async fetchWorkoutExerciseWeightHistory(workoutExerciseId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/workout_exercises/${workoutExerciseId}/weight_history`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      // Content-free diagnostics are emitted by the owned interceptor.
      throw error;
    }
  }

  async fetchWorkoutExerciseHistory(workoutExerciseId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/workout_exercises/${workoutExerciseId}/history`, {
        withCredentials: true,
        params: { _ts: Date.now() }
      });
      return response.data;
    } catch (error) {
      // The owned interceptor emits a bounded, content-free diagnostic.
      throw error;
    }
  }

  // Structural writes and outbox dispatch share the exercise's existing write
  // order. Only persistence waits; optimistic typing/checkboxes remain immediate.
  queueWorkoutSetWrite(context, operation) {
    // Set endpoints and reorder both touch the workout's exercise array. Share
    // one dispatch chain so this client's edits cannot invalidate reorder CAS.
    const key = String(context.workoutId);
    const generation = this.backgroundIdentityGeneration;
    const owner = this.user?._id;
    const write = (this.workoutSetWrites.get(key) || Promise.resolve()).catch(() => {}).then(() => {
      if (generation !== this.backgroundIdentityGeneration || owner !== this.user?._id) {
        throw Object.assign(new Error('Workout set write scope changed.'), { response: { status: 403 } });
      }
      return operation();
    });
    this.workoutSetWrites.set(key, write);
    const cleanup = () => { if (this.workoutSetWrites.get(key) === write) this.workoutSetWrites.delete(key); };
    write.then(cleanup, cleanup);
    return write;
  }

  async addWorkoutSet(newSet) {
    if (!newSet?.activity_id || !newSet?.workout_exercise_id) {
      throw new Error('Adding a workout set requires activity and exercise scope.');
    }
    const reads = this.workoutSetReads;
    const generation = this.backgroundIdentityGeneration;
    const context = { workoutId: newSet.activity_id, workoutExerciseId: newSet.workout_exercise_id, workoutSetIdentity: this.authenticatedIdentity, workoutSetOwner: this.user?._id };
    await this.workoutSetOutbox.waitForExercise(context);
    if (generation !== this.backgroundIdentityGeneration) throw new Error('Workout set write scope changed.');
    return this.queueWorkoutSetWrite(context, async () => {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/workout_exercises/${newSet.workout_exercise_id}/sets`, newSet, {
        withCredentials: true,
        params: { activity_id: newSet.activity_id }
      });
      if (generation !== this.backgroundIdentityGeneration) throw new Error('Workout set write scope changed.');
      reads.remember(`${newSet.activity_id}:${newSet.workout_exercise_id}:${response.data._id}`, response.data, 'add');
      return response.data;
    } catch (error) {
      console.error('Error adding workout set:', error);
      throw error;
    }
    });
  }

  async removeWorkoutSet(setId, context = {}) {
    const scope = getWorkoutSetScope(setId, context);
    if (!scope) throw new Error('Removing a workout set requires workout and exercise scope.');
    const reads = this.workoutSetReads;
    const generation = this.backgroundIdentityGeneration;
    const key = workoutSetScopeKey(scope);
    context = { ...context, workoutSetIdentity: this.authenticatedIdentity, workoutSetOwner: this.user?._id };
    reads.remember(key, {}, 'remove', true);
    const removal = reads.entries.get(key);
    try {
      await this.workoutSetOutbox.waitForExercise(context);
      if (generation !== this.backgroundIdentityGeneration) throw new Error('Workout set write scope changed.');
      await this.queueWorkoutSetWrite(context, () => this.client.delete(`${API_BASE_URL}/api/workout_sets/${setId}`, {
        withCredentials: true,
        params: { activity_id: scope.activityId, workout_exercise_id: scope.workoutExerciseId }
      }));
      if (generation !== this.backgroundIdentityGeneration) throw new Error('Workout set write scope changed.');
      reads.remember(key, {}, 'remove');
    } catch (error) {
      if (reads.entries.get(key) === removal) {
        if (removal.rollback) reads.entries.set(key, removal.rollback); else reads.entries.delete(key);
      }
      console.error('Error removing workout set:', error);
      throw error;
    }
  }

  /**
   * Buffers the edit durably before sending it, so a set changed while the app
   * is backgrounded, offline, or holding an expired token is replayed rather
   * than silently dropped. Edits to the same set are merged, so a weight and a
   * rep count entered in quick succession are both saved.
   */
  updateWorkoutSet(setId, updates, context = {}) {
    const scope = getWorkoutSetScope(setId, context);
    if (!scope) return Promise.reject(new Error('Updating a workout set requires workout and exercise scope.'));
    const scopedContext = { ...context, workoutId: scope.activityId, workoutExerciseId: scope.workoutExerciseId, eventPosition: this.getEventPosition(), workoutSetIdentity: this.authenticatedIdentity, workoutSetOwner: this.user?._id };
    const isCompletion = Object.prototype.hasOwnProperty.call(updates || {}, 'complete')
      || Object.prototype.hasOwnProperty.call(updates || {}, 'completed_at');
    return this.workoutSetOutbox.enqueue(setId, updates, { immediate: isCompletion, context: scopedContext });
  }

  // Set edits this principal admitted for the workout that the server hasn't
  // acknowledged (buffered, sending, retrying or queued structural writes), plus
  // a receipt that advances whenever a set save lands.
  workoutSetEditState(workoutId) {
    const principal = { workoutSetIdentity: this.authenticatedIdentity, workoutSetOwner: this.user?._id };
    return {
      pending: this.workoutSetOutbox.hasPendingForWorkout(workoutId, principal) || this.workoutSetWrites.has(String(workoutId)),
      version: this.workoutSetReads.version,
    };
  }

  async waitForWorkoutSetDrain(signal) {
    do {
      await this.workoutSetOutbox.waitForDrain(signal);
    } while (this.workoutSetOutbox.hasPending());
  }

  async waitForWorkoutSetUpdates(timeoutMs = 5000) {
    return this.workoutSetOutbox.waitForPending(timeoutMs);
  }

  async performWorkoutSetUpdate(setId, updates, meta = {}) {
    const scope = getWorkoutSetScope(setId, meta.context);
    if (!scope) throw new Error('Persisted workout set update is missing workout scope.');
    if ((Object.prototype.hasOwnProperty.call(meta.context, 'workoutSetIdentity') && meta.context.workoutSetIdentity !== this.authenticatedIdentity)
      || (meta.context.workoutSetOwner && meta.context.workoutSetOwner !== this.user?._id)) {
      throw Object.assign(new Error('Workout set write scope changed.'), { response: { status: 403 } });
    }
    const reads = this.workoutSetReads;
    const generation = this.backgroundIdentityGeneration;
    return this.queueWorkoutSetWrite(meta.context, async () => {
    const response = await this.client.put(`${API_BASE_URL}/api/workout_sets/${setId}`, updates, {
      katafitEventPosition: meta.context?.eventPosition || null,
      withCredentials: true,
      params: { activity_id: scope.activityId, workout_exercise_id: scope.workoutExerciseId }
    });
    if (generation !== this.backgroundIdentityGeneration) throw new Error('Workout set write scope changed.');
    const saved = Object.fromEntries(Object.keys(updates).map(field => [field,
      Object.prototype.hasOwnProperty.call(response.data, field) ? response.data[field] : updates[field]]));
    if (Object.prototype.hasOwnProperty.call(updates, 'complete')) saved.completed_at = response.data.completed_at;
    reads.remember(workoutSetScopeKey(scope), saved);
    return response.data;
    });
  }

  async regenerateWorkout(workoutId, userInput) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/workouts/${workoutId}/regenerate`, { userInput }, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error regenerating workout:', error);
      throw error;
    }
  }

  async fetchActivityGenerationInstructions(activityId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/activities/${activityId}/generation-instructions`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching activity generation instructions:', error);
      throw error;
    }
  }

  // Exercises
  async fetchExercises() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/exercises`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching exercises:', error);
      throw error;
    }
  }

  async fetchExerciseDetails(exerciseId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/exercise/${exerciseId}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching exercise details:', error);
      throw error;
    }
  }

  async fetchExerciseImage(image) {
    const normalizedImage = image.trim(); // Normalize the image string
    if (this.imageCache.has(normalizedImage)) {
      return this.imageCache.get(normalizedImage);
    }
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/exercise_images/${normalizedImage}`,
        {
          responseType: 'blob',
          withCredentials: true,
        }
      );
      const imageUrl = URL.createObjectURL(response.data);
      this.imageCache.set(normalizedImage, imageUrl); // Cache using the normalized key
      return imageUrl;
    } catch (error) {
      console.error(`[fetchExerciseImage] Error fetching image: "${normalizedImage}"`, error);
      throw error;
    }
  }

  async fetchInferences() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/inferences`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching inferences:', error);
      throw error;
    }
  }

  async getInferenceJobStatus() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/inferences/status`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching inference job status:', error);
      throw error;
    }
  }

  async runInferenceRerun() {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/inferences/run`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error starting inference rerun:', error);
      throw error;
    }
  }

  async cancelInferenceRerun() {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/inferences/cancel`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling inference rerun:', error);
      throw error;
    }
  }

  async fetchExerciseImages(images) {
    try {
      const urls = await Promise.all(images.map(async (image) => {
        const normalizedImage = image.trim(); // Normalize the image string
        if (this.imageCache.has(normalizedImage)) {
          return this.imageCache.get(normalizedImage);
        }
        const response = await this.client.get(`${API_BASE_URL}/api/exercise_images/${normalizedImage}`, {
          responseType: 'blob',
          withCredentials: true
        });
        const imageUrl = URL.createObjectURL(response.data);
        this.imageCache.set(normalizedImage, imageUrl); // Cache using the normalized key
        return imageUrl;
      }));
      return urls;
    } catch (error) {
      console.error(`[fetchExerciseImages] Error fetching images`, error);
      throw error;
    }
  }

  // Plans
  async fetchPlans() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/plans`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  }

  async fetchPlanDetails(planId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/plans/${planId}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching plan details:', error);
      throw error;
    }
  }

  async fetchPlanItems(planId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/plans/${planId}/entries`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching plan items:', error);
      throw error;
    }
  }

  async createPlan(title, goalStatement) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/plans`, {
        title,
        goal_statement: goalStatement,
        owner: 1
      }, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error creating plan:', error);
      throw error;
    }
  }

  async setPlanAffiliation(planId, todayStrategy = 'add') {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/user/affiliation`, {activity_plan_id: planId, today_strategy: todayStrategy}, {
        withCredentials: true
      });
      return response.status === 201;
    } catch (error) {
      console.error('Error setting plan affiliation:', error);
      throw error;
    }
  }

  async updatePlanDetails(planId, updates) {
    try {
      await this.client.put(`${API_BASE_URL}/api/plans/${planId}`, updates, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error updating plan details:', error);
      throw error;
    }
  }

  async fetchPlanItem(planId, planItemId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/plans/${planId}/entries/${planItemId}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching plan item:', error);
      throw error;
    }
  }

  async savePlanItem(planId, planItemId, updates) {
    try {
      const url = planItemId
        ? `${API_BASE_URL}/api/plans/${planId}/entries/${planItemId}`
        : `${API_BASE_URL}/api/plans/${planId}/entries`;
      const method = planItemId ? 'put' : 'post';
      const response = await this.client[method](url, updates, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error saving plan item:', error);
      throw error;
    }
  }

  async deletePlanItem(planId, planItemId) {
    try {
      await this.client.delete(`${API_BASE_URL}/api/plans/${planId}/entries/${planItemId}`, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error deleting plan item:', error);
      throw error;
    }
  }

  async reorderPlanEntries(planId, entryIds) {
    try {
      await this.client.put(`${API_BASE_URL}/api/plans/${planId}/reorder-entries`, { entryIds }, {
          withCredentials: true
      });
    } catch (error) {
      console.error('Error reordering plan entries:', error);
      throw error;
    }
  }

  async beginPlanEditSession(planId) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/plans/${planId}/edit-session`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error beginning plan edit session:', error);
      throw error;
    }
  }

  async commitPlanEditSession(planId) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/plans/${planId}/edit-session/commit`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error committing plan edit session:', error);
      throw error;
    }
  }

  async discardPlanEditSession(planId) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/plans/${planId}/edit-session/discard`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error discarding plan edit session:', error);
      throw error;
    }
  }

  async refreshWorkouts(planId, strategyId = null) {
    try {
      const body = strategyId ? { strategy_id: strategyId } : {};
      const response = await this.client.post(`${API_BASE_URL}/api/plans/${planId}/refresh`, body, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error refreshing workouts:', error);
      throw error;
    }
  }

  // ─── Archived plans ─────────────────────────────────────────────
  // When the user accepts a strategy that replaces their plan, the
  // previous plan is snapshotted to a separate archived doc. These
  // helpers let the UI list, restore and delete those snapshots.

  async fetchArchivedPlans() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/plans/archived`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching archived plans:', error);
      return [];
    }
  }

  async restoreArchivedPlan(planId) {
    const response = await this.client.post(`${API_BASE_URL}/api/plans/${planId}/restore`, {}, {
      withCredentials: true
    });
    return response.data;
  }

  async deleteArchivedPlan(planId) {
    const response = await this.client.delete(`${API_BASE_URL}/api/plans/archived/${planId}`, {
      withCredentials: true
    });
    return response.data;
  }

  async generatePlannedActivities() {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/wake`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error generating planned activities:', error);
      throw error;
    }
  }

  async fetchInstructionOptions(planId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/plans/${planId}/instruction-options`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching instruction options:', error);
      throw error;
    }
  }

  // Activities
  async fetchActivities(page, limit, type = null, startDate = null, endDate = null, types = null, statuses = null, offset = null, options = {}) {
    try {
      const params = { limit };
      if (options.todayActionScope === true) params.scope = 'today-actions';
      // Use offset-based pagination if offset is provided, otherwise use page-based
      if (offset !== null) {
        params.offset = offset;
      } else {
        params.page = page;
      }
      if (type) params.type = type;
      if (Array.isArray(types) && types.length > 0) params.types = types.join(',');
      if (Array.isArray(statuses) && statuses.length > 0) params.statuses = statuses.join(',');
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (options.cursor?.createdAt && options.cursor?.id) {
        params.cursorCreatedAt = options.cursor.createdAt;
        params.cursorId = options.cursor.id;
      }
      // Pass browser timezone so backend groups activities by the user's local day
      params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await this.client.get(`${API_BASE_URL}/api/activities`, {
        params,
        withCredentials: true,
        ...(options.timeout ? { timeout: options.timeout } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
      });
      return response.data;
    } catch (error) {
      // Content-free diagnostics are emitted by the owned interceptor.
      throw error;
    }
  }

  // New method: fetch activity by id
  async fetchActivity(activityId, options = {}) {
    this.pruneWorkoutExerciseOrders();
    const state = this.workoutExerciseOrders;
    const key = this.workoutExerciseOrderKey(activityId);
    const read = { key, version: ++state.version };
    state.reads.add(read);
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/activities/${activityId}`, {
        withCredentials: true,
        timeout: options.timeout,
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        ...(options.signal ? { signal: options.signal } : {}),
      });
      if (key !== this.workoutExerciseOrderKey(activityId)) throw new Error('Workout read scope changed.');
      return this.reconcileWorkoutExerciseOrder(activityId, response.data, read);
    } catch (error) {
      console.error('Error fetching activity:', error);
      throw error;
    } finally {
      state.reads.delete(read);
      this.pruneWorkoutExerciseOrders();
    }
  }

  // New methods for activity management
  async deleteActivity(activityId) {
    if (!activityId) return; // nothing to delete
    try {
      await this.client.delete(`${API_BASE_URL}/api/activities/${activityId}`, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error deleting activity:', error);
      throw error;
    }
  }

  async getMealSuggestion(mealName = null) {
    try {
      let url = `${API_BASE_URL}/api/meals/suggestion`;
      if (mealName) {
          url += `?meal_name=${encodeURIComponent(mealName)}`;
      }
      const response = await this.client.get(url, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching meal suggestion:', error);
      throw error;
    }
  }

  async generateMealSuggestionForActivity(activityId, constraints = null, targetCalories = null, images = [], { signal } = {}) {
    try {
      const hasImages = Array.isArray(images) && images.length > 0;
      const validTarget = Number.isFinite(Number(targetCalories)) && Number(targetCalories) > 0
        ? Number(targetCalories)
        : null;
      let body;
      if (hasImages) {
        body = new FormData();
        if (constraints != null) body.append('constraints', constraints);
        if (validTarget != null) body.append('target_calories', String(validTarget));
        images.forEach(image => body.append('images', image));
      } else {
        body = { constraints };
        if (validTarget != null) body.target_calories = validTarget;
      }
      return await runMealSuggestionRequest(
        async requestSignal => (await this.client.post(`${API_BASE_URL}/api/meals/${activityId}/suggestion`, body, { withCredentials: true, signal: requestSignal, timeout: 25000 })).data,
        async requestSignal => (await this.client.get(`${API_BASE_URL}/api/meals/${activityId}/suggestion`, { withCredentials: true, signal: requestSignal, timeout: 25000 })).data,
        signal,
      );
    } catch (error) {
      const safeCode = error?.response?.data?.code || error?.code || null;
      const safeMessage = error?.response?.data?.error;
      console.error('Error generating meal suggestion for activity', {
        code: safeCode,
        status: error?.response?.status || null,
      });
      if (safeCode && safeMessage) {
        const safeError = new Error(safeMessage);
        safeError.code = safeCode;
        throw safeError;
      }
      throw error;
    }
  }

  async resumeMealSuggestionForActivity(activityId, { signal } = {}) {
    const poll = async requestSignal => (await this.client.get(`${API_BASE_URL}/api/meals/${activityId}/suggestion`, { withCredentials: true, signal: requestSignal, timeout: 25000 })).data;
    return runMealSuggestionRequest(poll, poll, signal);
  }

  async clearMealSuggestion(activityId) {
    try {
      const response = await this.client.delete(`${API_BASE_URL}/api/meals/${activityId}/suggestion`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error clearing meal suggestion:', error);
      throw error;
    }
  }

  async createActivity(type, previousActivityId = null, activityName = null) {
    try {
      const payload = { type: type };
      if (previousActivityId) {
        payload.previous_activity_id = previousActivityId;
      }
      if (activityName) {
        payload.activity_name = activityName;
      }
      const response = await this.client.post(`${API_BASE_URL}/api/activities`, payload, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  }

  async setUserStatus(status, note = null, retryScope = null) {
    const identity = this.authenticatedIdentity;
    const owner = this.positionOwner;
    const assertScope = () => {
      if (identity !== this.authenticatedIdentity || owner !== this.positionOwner) {
        throw Object.assign(new Error('Account changed before request dispatch. Please retry.'), { code: 'REQUEST_ACCOUNT_CHANGED' });
      }
    };
    let intent = null;
    if (retryScope !== null) {
      if (!['morning', 'dropdown'].includes(retryScope)) throw new Error('Invalid check-in retry scope.');
      this.statusRetryIntents ||= new Map();
      const now = Date.now();
      let dateKey;
      try {
        const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: this.user?.timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' });
        dateKey = timestamp => formatter.format(new Date(timestamp));
      } catch (_) {
        dateKey = timestamp => new Date(timestamp).toISOString().slice(0, 10);
      }
      // Re-project both timestamps when profile timezone hydrates. Comparing a
      // stored fallback day with a newly loaded local day would rotate the key
      // at the same instant and duplicate an already committed unknown write.
      const fingerprint = JSON.stringify([identity, owner, status, note?.trim() || null]);
      for (const [scope, retained] of this.statusRetryIntents) {
        if (now - retained.createdAt >= 24 * 60 * 60 * 1000 || retained.createdAt > now || dateKey(retained.createdAt) !== dateKey(now) || retained.identity !== identity || retained.owner !== owner) this.statusRetryIntents.delete(scope);
      }
      intent = this.statusRetryIntents.get(retryScope);
      if (intent?.fingerprint !== fingerprint) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        intent = { key: Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(''), fingerprint, createdAt: now, identity, owner };
        this.statusRetryIntents.set(retryScope, intent);
      }
    }
    const snapshot = this.getCachedCompletionPosition();
    const payload = { status };
    if (note) payload.note = note;
    // GPS recovery belongs to the authenticated foreground lifecycle, never
    // this submit. Use its authorized initiating cache or save without GPS.
    const consentEpoch = this.getLocationConsentEpoch();
    const positionSnapshot = consentEpoch !== null && this.user?.location_capture_enabled === true &&
      snapshot?.owner_user_id === owner ? snapshot : null;
    if (positionSnapshot) {
      const { owner_user_id, ...position } = positionSnapshot;
      payload.position = position;
    }
    assertScope();
    let response;
    try {
      response = await this.client.post(`${API_BASE_URL}/api/status`, payload, {
        withCredentials: true, ...(intent ? { headers: { 'X-Katafit-Status-Operation': intent.key } } : {}), katafitEventPosition: positionSnapshot,
        katafitOptionalStatusPosition: true, katafitLocationConsentEpoch: consentEpoch,
        katafitEventIdentity: identity, katafitEventOwner: owner,
      });
    } catch (error) {
      // This exact server rejection proves the operation cannot unlock today.
      // Clear it for the next deliberate tap, never automatically POST again.
      if (intent && error.response?.status === 409 && error.response?.data?.code === 'STATUS_OPERATION_EXPIRED'
        && this.statusRetryIntents.get(retryScope) === intent) this.statusRetryIntents.delete(retryScope);
      throw error;
    }
    assertScope();
    if (intent && this.statusRetryIntents.get(retryScope) === intent) this.statusRetryIntents.delete(retryScope);
    return response.data;
  }

  async getLatestStatus(before = null) {
    try {
      const params = { _ts: Date.now() };
      if (before) params.before = before;
      const response = await this.client.get(`${API_BASE_URL}/api/status/latest`, {
        params,
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching latest status:', error);
      throw error;
    }
  }

  async retryDailyCoachInsight(statusId, expectedRetryGeneration) {
    if (!/^[a-f0-9]{24}$/i.test(String(statusId))) throw new Error('A valid saved check-in is required.');
    const response = await this.client.post(`${API_BASE_URL}/api/status/${statusId}/coach-insight/retry`, { expected_retry_generation: expectedRetryGeneration }, {
      withCredentials: true
    });
    return response.data;
  }

  async finishActivity(activityId, eventPosition = this.getEventPosition(), metadata = {}) {
    try {
      const response = await this.client.put(`${API_BASE_URL}/api/activities/${activityId}/complete`, {}, {
        ...metadata,
        katafitEventPosition: eventPosition,
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error finishing activity:', error);
      throw error;
    }
  }

  async setActivityPosition(activityId, coordinates, eventPosition = this.getEventPosition(), metadata = {}) {
    const response = await this.client.put(`${API_BASE_URL}/api/activities/${activityId}/position`, coordinates, {
      ...metadata,
      katafitEventPosition: eventPosition,
      withCredentials: true
    });
    return response.data;
  }

  async updateWorkoutCompletionTime(activityId, completedAt) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/activities/${activityId}/completed-at`,
        { completed_at: completedAt },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.error('Error updating workout completion time:', error);
      throw error;
    }
  }

  async reopenActivity(activityId) {
    try {
      const response = await this.client.put(`${API_BASE_URL}/api/activities/${activityId}/reopen`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error reopening activity:', error);
      throw error;
    }
  }

  async getActivityName(activityId) {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/activities/${activityId}/name`, {
        withCredentials: true
      });
      return response.data.name;
    } catch (error) {
      console.error('Error fetching activity name:', error);
      throw error;
    }
  }

  async updateActivityName(activityId, newName) {
    try {
      await this.client.put(`${API_BASE_URL}/api/activities/${activityId}/name`, { name: newName }, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error updating activity name:', error);
      throw error;
    }
  }

  async isUpgradeAvailable(activityId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/activities/${activityId}/is-upgrade-available`,
        { withCredentials: true }
      );
      return response.data.isUpgradeAvailable;
    } catch (error) {
      console.error(`Error checking if upgrade is available for activity ${activityId}:`, error);
      throw error;
    }
  }

  async upgradeActivity(activityId, options = {}) {
    try {
      const body = options.extraFoodChoice
        ? {
            extraFoodChoice: options.extraFoodChoice,
            ...(options.expectedRevision ? { expectedRevision: options.expectedRevision } : {}),
          }
        : {};
      const response = await this.client.post(
        `${API_BASE_URL}/api/activities/${activityId}/upgrade`,
        body,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      if (!error.response?.data?.code?.startsWith('MEAL_UPGRADE_')) {
        console.error(`Error upgrading activity ${activityId}:`, error);
      }
      throw error;
    }
  }

  // Survey management
  async addQuestionToSurvey(activityId, questionData) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/surveys/${activityId}/questions`, questionData, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error adding question to survey:', error);
      throw error;
    }
  }

  async updateSurveyQuestion(activityId, questionId, updates) {
    try {
      const response = await this.client.put(`${API_BASE_URL}/api/surveys/${activityId}/questions/${questionId}`, updates, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error updating survey question:', error);
      throw error;
    }
  }

  async removeQuestionFromSurvey(activityId, questionId) {
    try {
      const response = await this.client.delete(`${API_BASE_URL}/api/surveys/${activityId}/questions/${questionId}`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error removing question from survey:', error);
      throw error;
    }
  }

  // Media management
  async uploadMediaFiles(activityId, formData) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/media/${activityId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading media:', error);
      throw error;
    }
  }

  async updateMediaRequirements(activityId, requirements) {
    try {
      const response = await this.client.put(`${API_BASE_URL}/api/media/${activityId}/requirements`, { requirements }, {
        withCredentials: true
      });
      window.dispatchEvent(new CustomEvent('regimen:plan:updated', { detail: { kind: 'media_requirements', activityId } }));
      return response.data;
    } catch (error) {
      console.error('Error updating media requirements:', error);
      throw error;
    }
  }

  async updateMediaMetrics(activityId, metrics) {
    try {
      const response = await this.client.put(`${API_BASE_URL}/api/media/${activityId}/metrics`, { metrics }, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error updating media metrics:', error);
      throw error;
    }
  }

  async removeMediaFile(activityId, fileId) {
    try {
      const response = await this.client.delete(`${API_BASE_URL}/api/media/${activityId}/files/${fileId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error removing media file:', error);
      throw error;
    }
  }
  async triggerInference(activityId, fileId, options = {}) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/media/${activityId}/files/${fileId}/inference`, {}, {
        withCredentials: true,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      console.error('Error triggering inference:', error);
      throw error;
    }
  }
  // Private media-activity Coach chat. The server resolves the owner from the
  // session, so no identity is sent in the body or query.
  async getMediaCoachThread(activityId) {
    const response = await this.client.get(`${API_BASE_URL}/api/media/${activityId}/coach-thread`, {
      withCredentials: true,
      params: { _ts: Date.now() },
    });
    return response.data;
  }

  async sendMediaCoachMessage(activityId, { message, request_id: requestId }) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/media/${activityId}/coach-messages`,
      { message, request_id: requestId },
      { withCredentials: true }
    );
    return response.data;
  }

  async retryMediaCoachRequest(activityId, requestId) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/media/${activityId}/coach-requests/${requestId}/retry`,
      {},
      { withCredentials: true }
    );
    return response.data;
  }

  async fetchMediaFileUrl(mediaId, fileId, size, { signal } = {}) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/media/${mediaId}/files/${fileId}${size ? `?size=${size}` : ''}`,
        {
          responseType: 'blob',
          withCredentials: true,
          signal,
        }
      );
      return URL.createObjectURL(response.data); // Convert blob to object URL
    } catch (error) {
      // Don't log when the caller deliberately aborted (e.g. thumb scrolled
      // out of view) — that's expected control flow, not a real failure.
      if (!axios.isCancel?.(error) && error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
        console.error('Error fetching media file:', error);
      }
      throw error;
    }
  }

  // Return raw blob for a media file (useful when wanting to re-upload or convert to File)
  async fetchMediaFileBlob(mediaId, fileId, size) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/media/${mediaId}/files/${fileId}${size ? `?size=${size}` : ''}`,
        {
          responseType: 'blob',
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching media file blob:', error);
      throw error;
    }
  }

  // Meals
  async addFoodToMeal(mealId, foodId, quantity, unit) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/meals/${mealId}/foods`,
        { food_id: foodId, quantity, unit },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding food to meal:', error);
      throw error;
    }
  }

  // All-or-none multi-food add. Retrying with the same operationId and foods
  // replays the committed result instead of adding the foods again.
  async addFoodsToMeal(mealId, foods, operationId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/meals/${mealId}/foods/batch`,
        { operation_id: operationId, foods },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding foods to meal:', error);
      throw error;
    }
  }

  // Settles an unconfirmed batch: { status: 'committed', meal } if it already
  // applied, otherwise { status: 'cancelled' } and that key can never append.
  async cancelMealFoodBatch(mealId, operationId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/meals/${mealId}/foods/batch/${encodeURIComponent(operationId)}/cancel`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error settling meal food batch:', error);
      throw error;
    }
  }

  async removeFoodFromMeal(mealId, foodId, instanceId = null, { legacyIndex = null, expected = null } = {}) {
    try {
      const params = new URLSearchParams();
      if (instanceId) params.set('instance_id', instanceId);
      if (!instanceId && Number.isSafeInteger(legacyIndex) && expected) {
        params.set('legacy_index', String(legacyIndex));
        params.set('expected', JSON.stringify(expected));
      }
      const qs = params.toString();
      const url = `${API_BASE_URL}/api/meals/${mealId}/foods/${foodId}${qs ? '?' + qs : ''}`;
      const response = await this.client.delete(
        url,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing food from meal:', error);
      throw error;
    }
  }

  async reorderFoodsInMeal(mealId, newOrder) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/meals/${mealId}/reorder`,
        { newOrder },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error reordering foods in meal:', error);
      throw error;
    }
  }

  // Move foods within and/or across multiple meals (typically meals on the
  // same day). `moves` is an array of { mealId, foods: [...] } where `foods`
  // is the FULL new contents for that meal.
  async moveFoods(moves) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/meals/move-foods`,
        { moves },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error moving foods:', error);
      throw error;
    }
  }

  async getFoods(query = '', page = 0, limit = 20, sort = 'usage') {
    try {
      return await fetchFoodList(this.client, `${API_BASE_URL}/api/foods`, { q: query, page, limit, sort });
    } catch (error) {
      console.error('Error fetching foods:', error);
      throw error;
    }
  }

  async getMealFoodSuggestions(mealId, page = 0, limit = 20, sort = 'usage') {
    try {
      return await fetchFoodList(this.client, `${API_BASE_URL}/api/foods/meal-suggestions`, {
        meal_id: mealId, page, limit, sort
      });
    } catch (error) {
      console.error('Error fetching meal food suggestions:', error);
      throw error;
    }
  }

  async getFoodById(id) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/foods/${id}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching food ${id}:`, error);
      throw error;
    }
  }

  async inferFood(files, description, currentContext, onProgress = null, callerSignal = null) {
    const imageFiles = Array.isArray(files) ? files : (files ? [files] : []);
    const controller = new AbortController();
    let phaseTimer = null;
    let activePhase = imageFiles.length ? 'uploading' : 'analyzing';
    let abortReason = null;
    let requestStartedAt = null;
    const abortFromCaller = () => {
      abortReason = 'caller';
      controller.abort();
    };
    if (callerSignal?.aborted) abortFromCaller();
    else callerSignal?.addEventListener('abort', abortFromCaller, { once: true });

    const reportProgress = (progress) => {
      if (typeof onProgress !== 'function') return;
      try {
        onProgress(progress);
      } catch (error) {
        console.warn('Food inference progress handler failed:', error);
      }
    };
    const startPhaseDeadline = (phase, timeoutMs) => {
      if (phaseTimer) clearTimeout(phaseTimer);
      activePhase = phase;
      phaseTimer = setTimeout(() => {
        abortReason = 'timeout';
        controller.abort();
      }, timeoutMs);
    };

    try {
      const imageByteBudget = getFoodInferenceImageByteBudget(imageFiles.length);
      const hasOversizedImages = imageFiles.some(
        file => file?.type?.startsWith('image/') && file.size > imageByteBudget
      );
      let preparedImageFiles = imageFiles;
      if (hasOversizedImages) {
        preparedImageFiles = [];
        for (const file of imageFiles) {
          if (controller.signal.aborted) {
            throw Object.assign(new Error('canceled'), { code: 'ERR_CANCELED' });
          }
          preparedImageFiles.push(
            await prepareFoodInferenceImage(file, imageByteBudget, controller.signal)
          );
        }
      }
      const formData = new FormData();
      preparedImageFiles.forEach(file => formData.append('images', file));
      if (description) formData.append('description', description);
      if (currentContext) formData.append('currentContext', JSON.stringify(currentContext));

      const hasImages = imageFiles.length > 0;
      if (hasImages) {
        reportProgress({ phase: 'uploading', percent: 0 });
      } else {
        reportProgress({ phase: 'analyzing', percent: null });
      }

      const authHeaders = await this.getAuthHeaders({ skipCache: true });
      if (!authHeaders.Authorization) {
        const authError = new Error('Please sign in again before analyzing food.');
        authError.code = 'auth_required';
        throw authError;
      }
      if (controller.signal.aborted) {
        throw Object.assign(new Error('canceled'), { code: 'ERR_CANCELED' });
      }
      requestStartedAt = Date.now();
      // Browsers do not guarantee usable upload progress events. Until a
      // determinate event proves the upload phase is observable, use one
      // combined upload-plus-analysis deadline and a phase-neutral timeout.
      // This avoids the upload timer aborting server-side model work after an
      // unreported upload completion. Determinate progress restores separate
      // budgets.
      startPhaseDeadline(
        hasImages ? 'combined' : 'analyzing',
        hasImages ? FOOD_UPLOAD_TIMEOUT_MS + FOOD_ANALYSIS_TIMEOUT_MS : FOOD_ANALYSIS_TIMEOUT_MS
      );
      const response = await this.client.post(
        `${API_BASE_URL}/api/foods/infer`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data', ...authHeaders },
          withCredentials: true,
          // Upload and model work have independent deadlines. A full-resolution
          // mobile upload no longer consumes the analysis response budget.
          timeout: 0,
          signal: controller.signal,
          onUploadProgress: hasImages ? (event) => {
            const ratio = Number.isFinite(event?.progress)
              ? event.progress
              : (event?.total > 0 ? event.loaded / event.total : null);
            const percent = Number.isFinite(ratio)
              ? Math.round(Math.max(0, Math.min(1, ratio)) * 100)
              : null;
            reportProgress({ phase: 'uploading', percent });

            const uploadComplete = ratio >= 1
              || (event?.total > 0 && event.loaded >= event.total);
            if (uploadComplete && activePhase !== 'analyzing') {
              reportProgress({ phase: 'analyzing', percent: null });
              startPhaseDeadline('analyzing', FOOD_ANALYSIS_TIMEOUT_MS);
            } else if (Number.isFinite(ratio) && activePhase === 'combined') {
              const elapsedUploadMs = Date.now() - requestStartedAt;
              const remainingUploadMs = FOOD_UPLOAD_TIMEOUT_MS - elapsedUploadMs;
              if (remainingUploadMs <= 0) {
                activePhase = 'uploading';
                abortReason = 'timeout';
                controller.abort();
              } else {
                startPhaseDeadline('uploading', remainingUploadMs);
              }
            }
          } : undefined,
        }
      );
      return response.data;
    } catch (error) {
      if (error.code === 'ERR_UPLOAD_FILE_CHANGED') {
        console.warn('Upload interrupted - file changed/locked');
        throw new Error('FILE_CHANGED');
      }
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        const uploadError = new Error('Photo upload was interrupted. Please try again; your photos and description are still attached.');
        uploadError.code = 'upload_timeout';
        throw uploadError;
      }
      if (error.code === 'ERR_CANCELED' && abortReason === 'timeout') {
        const timeoutMessages = {
          uploading: 'Photo upload took too long. Please try again; your photos and description are still attached.',
          analyzing: 'Food analysis took too long. Please try again; your photos and description are still attached.',
          combined: 'Food photo upload/analysis took too long. Please try again; your photos and description are still attached.',
        };
        const timeoutCodes = {
          uploading: 'upload_timeout',
          analyzing: 'model_timeout',
          combined: 'inference_timeout',
        };
        const timeoutError = new Error(timeoutMessages[activePhase]);
        timeoutError.code = timeoutCodes[activePhase];
        throw timeoutError;
      }
      if (error.code === 'ECONNABORTED' || /timeout of \d+ms exceeded/i.test(error.message || '')) {
        const timeoutError = new Error('Food analysis took too long. Please try again; your photos and description are still attached.');
        timeoutError.code = 'model_timeout';
        throw timeoutError;
      }
      if (error.code === 'ERR_CANCELED') throw error;
      // Forward structured backend errors so the UI can show a specific message.
      const data = error?.response?.data;
      if (error?.response?.status === 401) {
        const authError = new Error('Your sign-in expired. Please try Autofill again.');
        authError.code = 'auth_required';
        authError.httpStatus = 401;
        throw authError;
      }
      if (error?.response?.status === 499) {
        const uploadError = new Error('Photo upload was interrupted. Please try again; your photos and description are still attached.');
        uploadError.code = 'upload_timeout';
        uploadError.httpStatus = 499;
        throw uploadError;
      }
      if (data?.code) {
        const e = new Error(data.error || 'Inference failed');
        e.code = data.code;
        e.httpStatus = error.response.status;
        throw e;
      }
      console.error('Error inferring food:', error);
      throw error;
    } finally {
      if (phaseTimer) clearTimeout(phaseTimer);
      callerSignal?.removeEventListener('abort', abortFromCaller);
    }
  }

  async createFood(foodData) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/foods`,
        foodData,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
       console.error("Error creating food:", error);
       throw error;
    }
  }

  async updateFood(id, updates) {
    try {
        const response = await this.client.put(
            `${API_BASE_URL}/api/foods/${id}`,
            updates,
            { withCredentials: true }
        );
        return response.data;
    } catch (error) {
        console.error("Error updating food:", error);
        throw error;
    }
  }

  async deleteFood(id) {
    try {
        await this.client.delete(`${API_BASE_URL}/api/foods/${id}`, {
            withCredentials: true
        });
    } catch (error) {
        console.error('Error deleting food:', error);
        throw error;
    }
  }

  async createMeal(mealName) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/meals`,
        { meal_name: mealName },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating meal:', error);
      throw error;
    }
  }

  async updateMealFoodQuantity(mealId, foodId, quantity, unit, instanceId = null) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/meals/${mealId}/foods/${foodId}`,
        { quantity, unit, instance_id: instanceId },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating food quantity in meal:', error);
      throw error;
    }
  }

  // Recipes
  async getRecipes(query = '', page = 0, limit = 20) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/recipes`,
        {
          params: { q: query, page, limit },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching recipes:', error);
      throw error;
    }
  }

  async getRecipeById(recipeId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/recipes/${recipeId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching recipe:', error);
      throw error;
    }
  }

  async createRecipe(name, description = '', ingredients = []) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/recipes`,
        { name, description, ingredients },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating recipe:', error);
      throw error;
    }
  }

  async updateRecipe(recipeId, updates) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/recipes/${recipeId}`,
        updates,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating recipe:', error);
      throw error;
    }
  }

  async deleteRecipe(recipeId) {
    try {
      await this.client.delete(
        `${API_BASE_URL}/api/recipes/${recipeId}`,
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error deleting recipe:', error);
      throw error;
    }
  }

  async addIngredientToRecipe(recipeId, foodId, quantity, unit) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/recipes/${recipeId}/ingredients`,
        { food_id: foodId, quantity, unit },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding ingredient to recipe:', error);
      throw error;
    }
  }

  async removeIngredientFromRecipe(recipeId, foodId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/recipes/${recipeId}/ingredients/${foodId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing ingredient from recipe:', error);
      throw error;
    }
  }

  async updateIngredientInRecipe(recipeId, foodId, quantity, unit) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/recipes/${recipeId}/ingredients/${foodId}`,
        { quantity, unit },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating ingredient in recipe:', error);
      throw error;
    }
  }

  // Recipe in Meal operations
  async convertMealSelectionToRecipe(mealId, name, selection) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/meals/${mealId}/recipes/convert`,
      { name, selection },
      { withCredentials: true }
    );
    return response.data;
  }

  async addRecipeToMeal(mealId, recipeId, scale = 1) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/meals/${mealId}/recipes`,
        { recipe_id: recipeId, scale },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding recipe to meal:', error);
      throw error;
    }
  }

  async scaleRecipeInMeal(mealId, recipeGroupId, scale) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/meals/${mealId}/recipes/${recipeGroupId}/scale`,
        { scale },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error scaling recipe in meal:', error);
      throw error;
    }
  }

  async removeRecipeFromMeal(mealId, recipeGroupId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/meals/${mealId}/recipes/${recipeGroupId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing recipe from meal:', error);
      throw error;
    }
  }

  // Metrics
  // cache: false bypasses the legacy day-keyed, owner-agnostic cache for both
  // reading and publishing, so the server resolves the exact instant.
  async fetchBodyweight(date, { fallback = 180, signal, cache = true } = {}) {
    try {
      const dayKey = new Date(date).toISOString().slice(0, 10); // YYYY-MM-DD
      if (cache && this.bodyweightCache.has(dayKey)) {
        return this.bodyweightCache.get(dayKey);
      }

      const response = await this.client.get(`${API_BASE_URL}/api/metrics/bodyweight`, {
        params: { date: new Date(date).toISOString() },
        withCredentials: true,
        ...(signal ? { signal } : {}),
      });
      if (cache) this.bodyweightCache.set(dayKey, response.data.bodyweight);
      return response.data.bodyweight;
    } catch (error) {
      console.error('Error fetching bodyweight:', error);
      return fallback; // Default 180; callers that must not guess pass null
    }
  }

  async fetchStats() {
    const MAX_ATTEMPTS = 30; // ~60s total
    const POLL_DELAY_MS = 2000;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      try {
        const response = await this.client.get(`${API_BASE_URL}/api/stats`, {
          withCredentials: true,
          validateStatus: (status) => status === 200 || status === 202,
        });
        if (response.status === 202) {
          await new Promise(r => setTimeout(r, POLL_DELAY_MS));
          continue;
        }
        return response.data;
      } catch (error) {
        console.error('Error fetching stats:', error);
        throw error;
      }
    }
    throw new Error('Stats still computing after maximum polling attempts');
  }

  async fetchMeasurementTypes() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/measurement_types`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching measurement types:', error);
      throw error;
    }
  }

  async addMeasurementToMetric(metricId, typeId, value, unit) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/metrics/${metricId}/measurements`,
        { type_id: typeId, value, ...(unit !== undefined ? { unit } : {}) },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding measurement to metric:', error);
      throw error;
    }
  }

  async removeMeasurementFromMetric(metricId, typeId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/metrics/${metricId}/measurements/${typeId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing measurement from metric:', error);
      throw error;
    }
  }

  async updateMeasurementValueInMetric(metricId, typeId, value, unit) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/metrics/${metricId}/measurements/${typeId}`,
        { value, ...(unit !== undefined ? { unit } : {}) },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating measurement value in metric:', error);
      throw error;
    }
  }

  async createMetric(metricName) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/metrics`,
        { metric_name: metricName },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating metric:', error);
      throw error;
    }
  }

  async startWorkout(activityId, location = null) {
    try {
      const body = {};
      if (location && location.latitude && location.longitude) {
        body.latitude = location.latitude;
        body.longitude = location.longitude;
      }
      const response = await this.client.put(`${API_BASE_URL}/api/activities/${activityId}/start`, body, {
        withCredentials: true,
        timeout: 15000,
      });
      return response.data;
    } catch (error) {
      // Content-free diagnostics are emitted by the owned interceptor.
      throw error;
    }
  }

  async stopWorkout(activityId) {
    try {
      const response = await this.client.put(`${API_BASE_URL}/api/activities/${activityId}/stop`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error stopping workout:', error);
      throw error;
    }
  }

  // Workout live streaming (AWS IVS)
  async getWorkoutStreamingSettings() {
    const response = await this.client.get(`${API_BASE_URL}/api/video-streams/settings`, { withCredentials: true });
    return response.data;
  }

  async updateWorkoutStreamingSettings(settings) {
    const response = await this.client.put(`${API_BASE_URL}/api/video-streams/settings`, settings, { withCredentials: true });
    return response.data;
  }

  async startWorkoutLiveStream(activityId, audiences) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/video-streams/workouts/${activityId}/start`,
      { audiences },
      { withCredentials: true }
    );
    return response.data;
  }

  async stopWorkoutLiveStream(activityId, sessionId) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/video-streams/workouts/${activityId}/${sessionId}/stop`,
      {},
      { withCredentials: true }
    );
    return response.data;
  }

  async getWorkoutStreamPlayback(activityId) {
    const response = await this.client.get(
      `${API_BASE_URL}/api/video-streams/workouts/${activityId}/playback`,
      { withCredentials: true }
    );
    return response.data;
  }

  async importData(formData) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error importing data:', error);
      throw error;
    }
  }

  async importMetrics(formData) {
    try {
      const response = await this.client.post(`${API_BASE_URL}/api/import/metrics`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error importing metrics:', error);
      throw error;
    }
  }

  async getImportStatus() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/import/status`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching import status:', error);
      throw error;
    }
  }

  async getWorkoutExerciseRecommendations(activityId, forceRegenerate = false, signal = null) {
    try {
      const config = { withCredentials: true };
      if (signal) {
        config.signal = signal;
      }
      const response = await this.client.post(
        `${API_BASE_URL}/api/workouts/${activityId}/recommendations`,
        { forceRegenerate },
        config
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.debug('Workout recommendations request was cancelled');
        return null;
      }
      console.error('Error fetching workout recommendations:', error);
      throw error;
    }
  }

  async getStoredWorkoutRecommendations(activityId, signal = null) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/workouts/${activityId}/recommendations`,
        { withCredentials: true, params: { _ts: Date.now() }, ...(signal ? { signal } : {}) }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching stored workout recommendations:', error);
      throw error;
    }
  }

  async getStoredSingleExerciseRecommendation(activityId, workoutExerciseId, signal = null) {
    const response = await this.client.get(
      `${API_BASE_URL}/api/workouts/${activityId}/recommendations/${workoutExerciseId}`,
      { withCredentials: true, params: { _ts: Date.now() }, timeout: 10000, ...(signal ? { signal } : {}) }
    );
    return response.data;
  }

  async getSingleExerciseRecommendation(activityId, workoutExerciseId, signal = null) {
    try {
      const config = { withCredentials: true };
      if (signal) {
        config.signal = signal;
      }
      const response = await this.client.post(
        `${API_BASE_URL}/api/workouts/${activityId}/recommendations/${workoutExerciseId}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.debug('Single exercise recommendation request was cancelled');
        return null;
      }
      console.error('Error fetching single exercise recommendation:', error);
      throw error;
    }
  }

  async submitExerciseMessage(activityId, workoutExerciseId, message, isResponse = false, signal = null, attachmentIds = []) {
    try {
      const config = { withCredentials: true };
      if (signal) {
        config.signal = signal;
      }
      const response = await this.client.post(
        `${API_BASE_URL}/api/workouts/${activityId}/exercises/${workoutExerciseId}/message`,
        {
          message,
          isResponse,
          is_response: isResponse,
          attachment_ids: Array.isArray(attachmentIds) ? attachmentIds : [],
        },
        config
      );
      const data = response.data;
      if (data?.generation?.status === 'pending' && data.generation.task_id) return data;
      if (['failed', 'cancelled', 'invalidated', 'timedout'].includes(data?.generation?.status)) {
        const unsupported = data.generation.error === 'WORKOUT_COACH_IMAGES_UNSUPPORTED';
        const error = new Error(unsupported
          ? 'Your message was saved. The selected Coach does not support workout photos yet.'
          : 'Your message was saved, but the selected Coach could not finish this response. No fallback Coach was used.');
        error.messageSaved = true;
        error.code = unsupported ? 'WORKOUT_COACH_IMAGES_UNSUPPORTED' : 'WORKOUT_COACH_TASK_UNAVAILABLE';
        throw error;
      }
      if (!data?.recommendation) {
        const error = new Error('Your message and attachment were saved, but Coach did not return a response. Try a new follow-up in a moment.');
        error.messageSaved = true;
        throw error;
      }
      return data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.debug('Exercise message request was cancelled');
        return null;
      }
      console.error('Error submitting exercise message:', error);
      throw error;
    }
  }

  async getWorkoutCoachThread(activityId, signal) {
    const response = await this.client.get(
      `${API_BASE_URL}/api/workouts/${activityId}/coach-thread`,
      { withCredentials: true, params: { _ts: Date.now() }, ...(signal ? { signal } : {}) }
    );
    return response.data;
  }

  async getExerciseConversation(activityId, workoutExerciseId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/workouts/${activityId}/exercises/${workoutExerciseId}/conversation`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      // 404 is expected when no conversation exists yet - return empty data
      if (error.response?.status === 404) {
        return { conversation_log: [] };
      }
      console.error('Error fetching exercise conversation:', error);
      throw error;
    }
  }

  async approveAction(workoutId, workoutExerciseId, actionId) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/workouts/${workoutId}/exercises/${workoutExerciseId}/actions/${actionId}/approve`,
      {},
      { withCredentials: true }
    );
    return response.data;
  }

  async rejectAction(workoutId, workoutExerciseId, actionId) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/workouts/${workoutId}/exercises/${workoutExerciseId}/actions/${actionId}/reject`,
      {},
      { withCredentials: true }
    );
    return response.data;
  }

  async approveAllActions(workoutId, workoutExerciseId, actionIds) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/workouts/${workoutId}/exercises/${workoutExerciseId}/actions/approve_all`,
      { action_ids: actionIds },
      { withCredentials: true }
    );
    return response.data;
  }

  async rejectAllActions(workoutId, workoutExerciseId, actionIds) {
    const response = await this.client.post(
      `${API_BASE_URL}/api/workouts/${workoutId}/exercises/${workoutExerciseId}/actions/reject_all`,
      { action_ids: actionIds },
      { withCredentials: true }
    );
    return response.data;
  }

  // Friends API methods

  async getFriends() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/friends`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching friends:', error);
      throw error;
    }
  }

  async searchUsers(query) {
    console.log('[BackendService] searchUsers called with query:', query);
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/friends/search`, {
        params: { q: query },
        withCredentials: true
      });
      console.log('[BackendService] searchUsers response:', response.status, response.data?.length, 'results');
      return response.data;
    } catch (error) {
      console.error('[BackendService] Error searching users:', error.message);
      throw error;
    }
  }

  async searchUsersByGym(gymId = null) {
    console.log('[BackendService] searchUsersByGym called with gymId:', gymId);
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/friends/search/gym`, {
        params: gymId ? { gym_id: gymId } : {},
        withCredentials: true
      });
      console.log('[BackendService] searchUsersByGym response:', response.status, response.data?.length, 'results');
      return response.data;
    } catch (error) {
      console.error('[BackendService] Error searching users by gym:', error.message);
      throw error;
    }
  }

  async getPendingFriendRequests() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/friends/requests/pending`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching pending friend requests:', error);
      throw error;
    }
  }

  async getSentFriendRequests() {
    try {
      const response = await this.client.get(`${API_BASE_URL}/api/friends/requests/sent`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sent friend requests:', error);
      throw error;
    }
  }

  async sendFriendRequest(toUserId) {
    console.log('[BackendService] sendFriendRequest called with toUserId:', toUserId);
    console.log('[BackendService] API URL:', `${API_BASE_URL}/api/friends/requests`);
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/friends/requests`,
        { to_user_id: toUserId },
        { withCredentials: true }
      );
      console.log('[BackendService] sendFriendRequest response:', response.status, response.data);
      return response.data;
    } catch (error) {
      console.error('[BackendService] Error sending friend request:', error.message);
      console.error('[BackendService] Error response:', error.response?.status, error.response?.data);
      throw error;
    }
  }

  async acceptFriendRequest(requestId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/friends/requests/${requestId}/accept`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error accepting friend request:', error);
      throw error;
    }
  }

  async declineFriendRequest(requestId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/friends/requests/${requestId}/decline`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error declining friend request:', error);
      throw error;
    }
  }

  async cancelFriendRequest(requestId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/friends/requests/${requestId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      throw error;
    }
  }

  async removeFriend(friendId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/friends/${friendId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing friend:', error);
      throw error;
    }
  }

  async getFriendStats(friendId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/friends/${friendId}/stats`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching friend stats:', error);
      throw error;
    }
  }

  // Get social activity feed (friends or dojo members)
  async getSocialActivityFeed(mode = 'friends', startDate = null, endDate = null, beforeDate = null, limit = 20) {
    try {
      const params = { limit };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (beforeDate) params.beforeDate = beforeDate;
      
      const response = await this.client.get(
        `${API_BASE_URL}/api/friends/feed/${mode}`,
        { params, withCredentials: true }
      );
      return response.data;
    } catch (error) {
      // Content-free diagnostics are emitted by the owned interceptor.
      throw error;
    }
  }

  async getSocialPresence(startDate = null, endDate = null, limit = 100) {
    const params = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.client.get(
      `${API_BASE_URL}/api/friends/feed/presence`,
      { params, withCredentials: true }
    );
    return response.data;
  }

  // Get full activity details for a friend's/dojo member's activity (read-only)
  async getSocialActivity(activityId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/friends/activity/${activityId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching social activity:', error);
      throw error;
    }
  }

  // Get a friend's or dojo member's profile identity/status.
  async getSocialProfile(userId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/friends/profile/${userId}/basic`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching social profile:', error);
      throw error;
    }
  }

  async getSocialProfileStats(userId, options = {}) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/friends/profile/${userId}/stats`,
        { withCredentials: true, signal: options.signal }
      );
      return response.data;
    } catch (error) {
      if (error?.code !== 'ERR_CANCELED' && error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
        console.error('Error fetching social profile stats:', error);
      }
      throw error;
    }
  }

  // --- Kudos & Comments ---

  async toggleKudos(activityId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/social/activities/${activityId}/kudos`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling kudos:', error);
      throw error;
    }
  }

  async getActivityKudos(activityId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/social/activities/${activityId}/kudos`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching kudos:', error);
      throw error;
    }
  }

  async getActivityComments(activityId, limit = 50, before = null) {
    try {
      const params = { limit };
      if (before) params.before = before;
      const response = await this.client.get(
        `${API_BASE_URL}/api/social/activities/${activityId}/comments`,
        { params, withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  }

  async addActivityComment(activityId, text) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/social/activities/${activityId}/comments`,
        { text },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  async deleteActivityComment(commentId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/social/comments/${commentId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  // Messages API methods

  async getUnreadMessageCount() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/messages/unread-count`,
        { withCredentials: true }
      );
      return response.data.count;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  async getConversations() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/messages/conversations`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  async getConversation(friendId, limit = 50, before = null) {
    try {
      const params = { limit };
      if (before) params.before = before;
      const response = await this.client.get(
        `${API_BASE_URL}/api/messages/conversation/${friendId}`,
        { params, withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation:', error);
      throw error;
    }
  }

  async sendMessage(toUserId, content) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/messages/send`,
        { to_user_id: toUserId, content },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async markMessagesAsRead(friendId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/messages/read/${friendId}`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  }

  async getDojoMessages(dojoId, limit = 50, before = null) {
    try {
      const params = { limit };
      if (before) params.before = before;
      const response = await this.client.get(
        `${API_BASE_URL}/api/messages/dojo/${dojoId}`,
        { params, withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching dojo messages:', error);
      throw error;
    }
  }

  async sendDojoMessage(dojoId, content) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/messages/dojo/${dojoId}/send`,
        { content },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending dojo message:', error);
      throw error;
    }
  }

  // Dojo API methods

  async createDojo(dojoData) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos`,
        dojoData,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating dojo:', error);
      throw error;
    }
  }

  async getMyDojo() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/my`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching my dojo:', error);
      throw error;
    }
  }

  async searchDojos(query = '') {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos`,
        { params: { q: query }, withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error searching dojos:', error);
      throw error;
    }
  }

  async getDojo(dojoId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching dojo:', error);
      throw error;
    }
  }

  async uploadDojoBrandingImage(dojoId, assetType, file) {
    if (!['logo', 'hero', 'background'].includes(assetType)) throw new Error('Unsupported dojo branding asset type');
    const form = new FormData();
    form.append('image', file);
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/dojos/${dojoId}/branding/${assetType}`, {
      method: 'POST',
      headers,
      body: form,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || `Failed to save dojo ${assetType}`);
    return data;
  }

  async removeDojoBrandingImage(dojoId, assetType) {
    if (!['logo', 'hero', 'background'].includes(assetType)) throw new Error('Unsupported dojo branding asset type');
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/dojos/${dojoId}/branding/${assetType}`, { method: 'DELETE', headers });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || `Failed to remove dojo ${assetType}`);
    return data;
  }

  async fetchDojoBrandingImage(url, { onBlob } = {}) {
    const browserOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const apiOrigin = new URL(API_BASE_URL || browserOrigin, browserOrigin).origin;
    const target = new URL(url, apiOrigin);
    if (target.origin !== apiOrigin) throw new Error('untrusted dojo branding image URL');
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(target.toString(), { headers, credentials: 'include' });
    if (!response.ok) {
      const error = new Error('Failed to load dojo branding image');
      error.status = response.status;
      throw error;
    }
    const blob = await response.blob();
    onBlob?.(blob);
    return URL.createObjectURL(blob);
  }

  async uploadDojoCoachImage(dojoId, file) {
    const form = new FormData();
    form.append('image', file);
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/dojos/${dojoId}/coach-image`, {
      method: 'POST',
      headers,
      body: form,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.error || 'Failed to save dojo coach image');
    return data;
  }

  async updateDojo(dojoId, updates) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/dojos/${dojoId}`,
        updates,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating dojo:', error);
      throw error;
    }
  }

  async deleteDojo(dojoId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/dojos/${dojoId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting dojo:', error);
      throw error;
    }
  }

  async getDojoMembers(dojoId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/members`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching dojo members:', error);
      throw error;
    }
  }

  async checkDojoEligibility(dojoId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/check-eligibility`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking dojo eligibility:', error);
      throw error;
    }
  }

  async joinDojo(dojoId, options = {}) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/join`,
        options,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error joining dojo:', error);
      throw error;
    }
  }

  async requestToJoinDojo(dojoId, message = '', options = {}) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/request-join`,
        { message, ...options },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error requesting to join dojo:', error);
      throw error;
    }
  }

  async updateCoachAutoAcceptance(autoAcceptance) {
    const response = await this.client.put(`${API_BASE_URL}/api/user/coach-auto-acceptance`, { auto_acceptance: autoAcceptance }, { withCredentials: true });
    return response.data;
  }

  async leaveDojo() {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/leave`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error leaving dojo:', error);
      throw error;
    }
  }

  async kickDojoMember(dojoId, userId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/dojos/${dojoId}/members/${userId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error kicking member:', error);
      throw error;
    }
  }

  async updateDojoMemberRole(dojoId, userId, role) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/dojos/${dojoId}/members/${userId}/role`,
        { role },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  }

  async transferDojoChief(dojoId, newChiefId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/transfer-chief`,
        { new_chief_id: newChiefId },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error transferring chief:', error);
      throw error;
    }
  }

  async getDojoJoinRequests(dojoId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/requests`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching join requests:', error);
      throw error;
    }
  }

  async approveDojoJoinRequest(dojoId, requestId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/requests/${requestId}/approve`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error approving request:', error);
      throw error;
    }
  }

  async rejectDojoJoinRequest(dojoId, requestId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/requests/${requestId}/reject`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error rejecting request:', error);
      throw error;
    }
  }

  async getSentDojoInvites() {
    const response = await this.client.get(`${API_BASE_URL}/api/dojos/invites/sent`, { withCredentials: true });
    return response.data;
  }

  async cancelDojoInvite(inviteId) {
    const response = await this.client.post(`${API_BASE_URL}/api/dojos/invites/${inviteId}/cancel`, {}, { withCredentials: true });
    return response.data;
  }

  async inviteUserToDojo(dojoId, targetUserId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/invite`,
        { target_user_id: targetUserId },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error inviting user:', error);
      throw error;
    }
  }

  async getDojoInvites() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/invites`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching dojo invites:', error);
      throw error;
    }
  }

  async declineDojoInvite(inviteId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/invites/${inviteId}/decline`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error declining invite:', error);
      throw error;
    }
  }

  async getDojoRequirements(dojoId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/requirements`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      // Return default empty requirements on error
      if (error.response?.status === 404) {
        return { requirements: null, compliance: null };
      }
      console.error('Error fetching dojo requirements:', error);
      return { requirements: null, compliance: null };
    }
  }

  async updateDojoRequirements(dojoId, requirements) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/dojos/${dojoId}/requirements`,
        requirements,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating dojo requirements:', error);
      throw error;
    }
  }

  async checkDojoCompliance(dojoId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/compliance`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking dojo compliance:', error);
      throw error;
    }
  }

  // Recovery Days
  async getRecoveryDays(dojoId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/recovery-days`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching recovery days:', error);
      throw error;
    }
  }

  async useRecoveryDay(dojoId, date) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/recovery-days`,
        { date },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error using recovery day:', error);
      throw error;
    }
  }

  async removeRecoveryDay(dojoId, date) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/dojos/${dojoId}/recovery-days/${date}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing recovery day:', error);
      throw error;
    }
  }

  // Personal (user-level) rest days — usable outside of a dojo.
  async getRestDays() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/users/me/rest-days`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching rest days:', error);
      throw error;
    }
  }

  async updateRestDayQuota(perYear) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/users/me/rest-days`,
        { per_year: perYear },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating rest day quota:', error);
      throw error;
    }
  }

  async markWorkoutAsRest(workoutId, reason = 'fine') {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/workouts/${workoutId}/rest`,
        { reason },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking workout as rest:', error);
      throw error;
    }
  }

  async unmarkWorkoutRest(workoutId) {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/workouts/${workoutId}/rest`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error clearing workout rest:', error);
      throw error;
    }
  }

  async getDojoLeaderboard(dojoId, period = 'week', limit = 50) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/leaderboard`,
        { 
          params: { period, limit },
          withCredentials: true 
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching dojo leaderboard:', error);
      throw error;
    }
  }


  async submitStrategySuggestion(dojoId, strategyId, text) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/${dojoId}/suggestions`,
        { strategy_id: strategyId, text },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      throw error;
    }
  }

  async getStrategySuggestions(dojoId, strategyId) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/${dojoId}/suggestions`,
        {
          params: { strategy_id: strategyId },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      throw error;
    }
  }

  async updateSuggestionStatus(dojoId, suggestionId, status) {
    try {
      const response = await this.client.patch(
        `${API_BASE_URL}/api/dojos/${dojoId}/suggestions/${suggestionId}`,
        { status },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating suggestion:', error);
      throw error;
    }
  }

  async getWeeklyConsistencyLeaderboard(mode, weekStart, weekEnd) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/friends/weekly-leaderboard`,
        {
          params: { mode, weekStart, weekEnd },
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching weekly leaderboard:', error);
      throw error;
    }
  }

  // ===== Email Verification =====

  async getEmailVerificationStatus() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/email-verification/status`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting verification status:', error);
      throw error;
    }
  }

  async resendVerificationEmail() {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/email-verification/resend`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error resending verification:', error);
      throw error;
    }
  }

  async verifyEmailWithCode(code) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/email-verification/verify-code`,
        { code },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error verifying email:', error);
      throw error;
    }
  }

  async registerPushDevice(device) {
    const headers = await this.getAuthHeaders();
    const response = await this.client.put(`${API_BASE_URL}/api/users/me/push-devices`, device, { headers, withCredentials: true });
    return response.data;
  }

  async unregisterPushDevice(device) {
    const headers = await this.getAuthHeaders();
    const response = await this.client.delete(`${API_BASE_URL}/api/users/me/push-devices`, { data: device, headers, withCredentials: true });
    return response.data;
  }

  // ===== Notification Preferences =====

  async getNotificationPreferences() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/users/me/notification-preferences`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      throw error;
    }
  }

  async updateNotificationPreferences(preferences) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/users/me/notification-preferences`,
        preferences,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  // ===== Privacy Settings =====

  async getPrivacySettings() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/users/me/privacy-settings`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting privacy settings:', error);
      throw error;
    }
  }

  // ===== Discord Presence Settings =====

  async getDiscordPresenceSettings() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/users/me/discord-presence-settings`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting Discord presence settings:', error);
      throw error;
    }
  }

  async updateDiscordPresenceSettings(settings) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/users/me/discord-presence-settings`,
        settings,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating Discord presence settings:', error);
      throw error;
    }
  }

  // ===== Discord Account Linking =====
  // The client secret and refresh token live on the server; these only ever
  // move an authorization code up and a short-lived access token down.

  async linkDiscordAccount({ code, code_verifier, redirect_uri }) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/users/me/discord/link`,
        { code, code_verifier, redirect_uri },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error linking Discord account:', error);
      throw error;
    }
  }

  async startDiscordWebLink() {
    const response = await this.client.post(
      `${API_BASE_URL}/api/users/me/discord/web-link`,
      {},
      { withCredentials: true }
    );
    return response.data;
  }

  async getDiscordLinkStatus() {
    const response = await this.client.get(
      `${API_BASE_URL}/api/users/me/discord/link-status`,
      { withCredentials: true }
    );
    return response.data;
  }

  /** @returns {Promise<{access_token: string, expires_in: number}|null>} null when not linked. */
  async getDiscordAccessToken() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/users/me/discord/token`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.error('Error getting Discord access token:', error);
      throw error;
    }
  }

  async unlinkDiscordAccount() {
    try {
      const response = await this.client.delete(
        `${API_BASE_URL}/api/users/me/discord/link`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error unlinking Discord account:', error);
      throw error;
    }
  }

  async getHealthConnectStatus() {
    const response = await this.client.get(
      `${API_BASE_URL}/api/users/me/health-connect-status`,
      { withCredentials: true }
    );
    return response.data;
  }

  async updateHealthConnectStatus(status) {
    const response = await this.client.put(
      `${API_BASE_URL}/api/users/me/health-connect-status`,
      status,
      { withCredentials: true }
    );
    return response.data;
  }

  async updatePrivacySettings(settings) {
    this.beginLocationConsentWrite();
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/users/me/privacy-settings`,
        settings,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating privacy settings:', error);
      throw error;
    } finally { this.endLocationConsentWrite(); }
  }

  // ===== Dojo Invite Links =====

  async getDojoInviteLink() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/invite-link`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting invite link:', error);
      throw error;
    }
  }

  async regenerateDojoInviteLink() {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/invite-link/regenerate`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error regenerating invite link:', error);
      throw error;
    }
  }

  async getDojoJoinLinkInfo(token) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/dojos/join/${token}/info`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting join link info:', error);
      throw error;
    }
  }

  async joinDojoViaLink(token, options = {}) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/join/${token}`,
        options,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error joining via link:', error);
      throw error;
    }
  }

  async getDojoEmailInviteInfo(token) {
    const response = await this.client.get(`${API_BASE_URL}/api/dojos/email-invite/${token}/info`, { withCredentials: true });
    return response.data;
  }

  async acceptDojoEmailInvite(token, options = {}) {
    const response = await this.client.post(`${API_BASE_URL}/api/dojos/email-invite/${token}/accept`, options, { withCredentials: true });
    return response.data;
  }

  async sendDojoEmailInvite(email) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/invite-email`,
        { email },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending email invite:', error);
      throw error;
    }
  }

  async sendAppInvite(email, includeDojo = false) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/dojos/app-invite`,
        { email, include_dojo: includeDojo },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending app invite:', error);
      throw error;
    }
  }

  async getAppInviteInfo(token) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/invite/${token}`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting app invite info:', error);
      throw error;
    }
  }

  // Gyms
  async getGyms() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/gyms`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching gyms:', error);
      throw error;
    }
  }

  async createGym(gymData) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/gyms`,
        gymData,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating gym:', error);
      throw error;
    }
  }

  async updateGym(gymId, updates) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/gyms/${gymId}`,
        updates,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating gym:', error);
      throw error;
    }
  }

  async deleteGym(gymId) {
    try {
      await this.client.delete(
        `${API_BASE_URL}/api/gyms/${gymId}`,
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error deleting gym:', error);
      throw error;
    }
  }

  async updateWorkoutGym(activityId, gymId, confirmed = true) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/workouts/${activityId}/gym`,
        { gym_id: gymId, confirmed },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating workout gym:', error);
      throw error;
    }
  }

  async clearWorkoutGym(activityId) {
    try {
      const response = await this.client.put(
        `${API_BASE_URL}/api/workouts/${activityId}/gym`,
        { gym_id: null },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error clearing workout gym:', error);
      throw error;
    }
  }

  async searchNearbyGyms(latitude, longitude, radius = 1000, keywords = null, options = {}) {
    try {
      const params = { latitude, longitude, radius };
      if (keywords) params.keywords = keywords;
      const response = await this.client.get(
        `${API_BASE_URL}/api/gyms/search/nearby`,
        {
          params,
          withCredentials: true,
          signal: options.signal,
        }
      );
      return response.data;
    } catch (error) {
      if (!options.signal?.aborted && error.code !== 'ERR_CANCELED' && !axios.isCancel?.(error)) {
        console.error('Error searching nearby gyms:', error);
      }
      throw error;
    }
  }

  async createGymFromSearch(searchResult) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/gyms/from-search`,
        searchResult,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating gym from search:', error);
      throw error;
    }
  }

  async selectWorkoutGymFromSearch(activityId, searchResult) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/workouts/${activityId}/gym/from-search`,
        searchResult,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error selecting workout gym from search:', error);
      throw error;
    }
  }

  // ===== Subscription & Payments =====

  async getSubscriptionConfig() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/subscriptions/config`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting subscription config:', error);
      return { gatingEnabled: false };
    }
  }

  async getSubscriptionStatus() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/subscriptions/status`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  async getSubscriptionFeatures() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/subscriptions/features`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting subscription features:', error);
      throw error;
    }
  }

  async checkFeature(feature) {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/subscriptions/check/${feature}`,
        { withCredentials: true }
      );
      return response.data.hasAccess;
    } catch (error) {
      console.error('Error checking feature:', error);
      return false;
    }
  }

  async getSubscriptionPrices() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/subscriptions/prices`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting subscription prices:', error);
      throw error;
    }
  }

  async createCheckout(priceId) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/subscriptions/create-checkout`,
        { price_id: priceId },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating checkout:', error);
      throw error;
    }
  }

  async getSubscriptionPortal() {
    try {
      const response = await this.client.get(
        `${API_BASE_URL}/api/subscriptions/portal`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting subscription portal:', error);
      throw error;
    }
  }

  // ─── Strategy API ────────────────────────────────────────────────

  // ─── Coach Hub ───────────────────────────────────────────────────────

  /**
   * Composite call powering the Coach hub modal. Returns
   * { strategy_snapshot, today_insight, insights, recent_memory, conversation }.
   */
  async fetchCoachHome(signal = null) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/coach/home`, {
        method: 'GET',
        headers,
        cache: 'no-store',
        ...(signal ? { signal } : {})
      });
      if (response.ok) return await response.json();
      return null;
    } catch (error) {
      if (error?.name !== 'AbortError') console.error('Error fetching coach home:', error);
      return null;
    }
  }

  async fetchCoachActivityEvents() {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/activity-events`, {
      method: 'GET',
      headers,
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Failed to load coach activity events (${response.status})`);
    const payload = await response.json();
    const events = Array.isArray(payload?.activity_events) ? payload.activity_events : [];
    return {
      events,
      hasAwaitingActivityReview: typeof payload?.has_awaiting_activity_review === 'boolean'
        ? payload.has_awaiting_activity_review
        : events.some(event => (
          ['pending', 'claimed'].includes(event?.generation_state)
          && event?.reply_worthwhile !== false
        )),
    };
  }

  async retryCoachActivityEvent(eventId) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/activity-events/${encodeURIComponent(eventId)}/retry`, {
      method: 'POST',
      headers,
    });
    await publishAiBudgetRequiredFromFetch(response);
    if (!response.ok) {
      let message = `Failed to retry Coach review (${response.status})`;
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {}
      throw new Error(message);
    }
    return response.json();
  }

  /**
   * Upload one or more attachments for coach chat and return ids + metadata.
   */
  async uploadCoachAttachments(files) {
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    if (list.length === 0) return [];

    const headers = await this.getAuthHeaders();
    const formData = new FormData();
    for (const file of list) {
      formData.append('files', file);
    }

    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/attachments`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      let msg = `Attachment upload failed (${response.status})`;
      try {
        const body = await response.json();
        if (body && body.error) msg = body.error;
      } catch {}
      throw new Error(msg);
    }

    const data = await response.json();
    return (data?.attachments || []).map((a) => ({
      ...a,
      url: a?.path ? `${API_BASE_URL}${a.path}` : null
    }));
  }

  /**
   * Fetch attachment bytes through the authenticated owner-scoped endpoint.
   */
  async fetchCoachAttachment(attachmentId) {
    const response = await this.client.get(
      `${API_BASE_URL}/api/coach/attachments/${encodeURIComponent(attachmentId)}`,
      { responseType: 'blob', withCredentials: true }
    );
    return URL.createObjectURL(response.data);
  }

  /**
   * Send a free-form message to the coach. Returns { reply, conversation }.
   * `focus` optionally scopes the exchange (e.g. { type: 'exercise',
   * exercise_name, exercise_id }) so the coach anchors on a specific subject.
   */
  async askCoach(message, attachmentIds = [], focus = null, clientRequestId = null) {
    const headers = await this.getAuthHeaders();
    const body = {
      message,
      attachment_ids: Array.isArray(attachmentIds) ? attachmentIds : [],
      ...(clientRequestId ? { client_request_id: clientRequestId } : {})
    };
    if (focus && focus.type) body.focus = focus;
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/ask`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    await publishAiBudgetRequiredFromFetch(response);
    if (!response.ok) {
      let msg = `Coach unavailable (${response.status})`;
      let payload;
      try {
        payload = await response.json();
        if (typeof payload?.error === 'string') msg = payload.error;
      } catch {}
      const error = new Error(msg);
      if (payload?.code) error.code = payload.code;
      // A terminal application response is not a gateway's ambiguous timeout.
      // Preserve late-reply reconciliation when no authoritative outcome arrived.
      if ([502, 503, 504].includes(response.status) && payload?.saved !== false) error.possiblySaved = true;
      throw error;
    }
    return response.json();
  }

  async fetchExternalCoachDataGrant(signal) {
    return this.externalCoachDataGrantRequest('GET', undefined, signal);
  }

  async updateExternalCoachDataGrant(input, signal) {
    return this.externalCoachDataGrantRequest('PUT', input, signal);
  }

  async revokeExternalCoachDataGrant(signal) {
    return this.externalCoachDataGrantRequest('DELETE', undefined, signal);
  }

  async externalCoachDataGrantRequest(method, input, signal) {
    const headers = await this.getAuthHeaders({ skipCache: true });
    if (signal?.aborted) throw new Error('Data access request stopped before sending');
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent/my-data-grant`, {
      method, headers: { ...headers, 'Content-Type': 'application/json' },
      ...(input === undefined ? {} : { body: JSON.stringify(input) }), cache: 'no-store', signal,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Could not update your data access. Refresh to check the saved state.');
    return payload;
  }

  async fetchExternalCoachAgent() {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent`, { headers, cache: 'no-store' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Could not load external Coach settings');
    return payload;
  }

  async createExternalCoachTest(clientRequestId, signal) {
    return this.externalCoachTestRequest('', { method: 'POST', body: JSON.stringify({ client_request_id: clientRequestId }), signal });
  }

  async fetchExternalCoachTest(requestId, signal) {
    return this.externalCoachTestRequest(`/${encodeURIComponent(requestId)}`, { signal });
  }

  async externalCoachTestRequest(path, options) {
    const headers = await this.getAuthHeaders({ skipCache: true });
    // Auth acquisition can outlive the modal. Never send after its deadline.
    if (options.signal?.aborted) throw new Error('Test stopped before sending');
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent/tests${path}`, {
      ...options, headers: { ...headers, 'Content-Type': 'application/json' }, cache: 'no-store',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'External Coach test failed. Please retry.');
    return payload;
  }

  async updateExternalCoachAgent(settings) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent`, {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(settings || {}),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Could not update external Coach settings');
    return payload;
  }

  async createExternalCoachCredential(input) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent/credentials`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(input || {}),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Could not create external Coach credential');
    return payload;
  }

  async fetchCoachRestCredentials() {
    const token = await this.getToken();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/rest-credentials`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.message || body.error || 'Failed to load account access');
    return body;
  }

  async revokeCoachRestCredential(id) {
    const token = await this.getToken();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/rest-credentials/${encodeURIComponent(id)}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.message || body.error || 'Failed to revoke account access');
    return body;
  }

  async revokeExternalCoachCredential(credentialId) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent/credentials/${encodeURIComponent(credentialId)}`, {
      method: 'DELETE', headers,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Could not revoke external Coach credential');
    return payload;
  }

  async retryExternalCoachRequest(requestId) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent/requests/${encodeURIComponent(requestId)}/retry`, {
      method: 'POST', headers,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Could not retry external Coach request');
    return payload;
  }

  async useHostedCoachFallback(requestId) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/external-agent/requests/${encodeURIComponent(requestId)}/hosted-fallback`, {
      method: 'POST', headers,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Could not use hosted Coach fallback');
    return payload;
  }

  async regenerateCoachReply(messageId) {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/coach/conversation/regenerate`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ message_id: messageId }),
    });
    await publishAiBudgetRequiredFromFetch(response);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || `Could not regenerate Coach reply (${response.status})`);
    return payload;
  }

  /**
   * Wipe the persisted ask-coach conversation.
   */
  async clearCoachConversation() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/coach/conversation`, {
        method: 'DELETE',
        headers
      });
      return response.ok;
    } catch (error) {
      console.error('Error clearing coach conversation:', error);
      return false;
    }
  }

  /**
   * List the strategy-proposal lifecycle visible to the signed-in member.
   * Callers use getPendingStrategyProposals when they need actionable-only.
   */
  async fetchStrategyProposals() {
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/proposals`, {
      method: 'GET',
      headers: { ...headers, 'Cache-Control': 'no-cache' },
      cache: 'no-store'
    });
    if (!response.ok) {
      let message = `Failed to fetch strategy proposals (${response.status})`;
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {}
      throw new Error(message);
    }
    const payload = await response.json();
    return Array.isArray(payload?.proposals) ? payload.proposals : [];
  }

  async performStrategyProposalAction(proposalId, action, comment) {
    // Proposal actions are user-visible mutations. Force Clerk to refresh its
    // session token instead of sending a cached token that may have expired
    // while the Coach Hub was left open.
    const headers = await this.getAuthHeaders({ skipCache: true });
    const url = `${API_BASE_URL}/api/strategy/proposals/${encodeURIComponent(proposalId)}/${action}`;
    console.info('[BackendService] strategy proposal request', {
      url,
      action,
      proposalId: String(proposalId),
      hasAuthorization: Boolean(headers.Authorization)
    });
    const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/proposals/${encodeURIComponent(proposalId)}/${action}`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: comment === undefined ? undefined : JSON.stringify({ comment })
    });
    if (!response.ok) {
      let message = `Failed to ${action} strategy proposal (${response.status})`;
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {}
      console.error('[BackendService] strategy proposal request failed', {
        url,
        action,
        proposalId: String(proposalId),
        status: response.status,
        message
      });
      throw new Error(message);
    }
    const payload = await response.json();
    const updatedWorkoutIds = Array.isArray(payload?.references?.updated_future_activity_ids)
      ? payload.references.updated_future_activity_ids
      : [];
    updatedWorkoutIds.forEach(workoutId => window.dispatchEvent(new CustomEvent('regimen:workout-updated', {
      detail: { workoutId: String(workoutId), source: 'strategy_proposal_approved' }
    })));
    if (action === 'approve' || action === 'reject' || action === 'acknowledge') {
      window.dispatchEvent(new CustomEvent('regimen:strategy-proposals-updated', {
        detail: {
          proposalId: String(proposalId),
          status: payload?.status || action,
          // The approved DTO is { status, references }, not the proposal.
          // Only nutrition approval returns this member overlay reference.
          ...(action === 'approve' && payload?.status === 'approved'
            && payload?.references?.nutrition_overlay_id
            && payload?.references?.member_plan_id && payload?.references?.member_id
            ? { nutritionMemberId: String(payload.references.member_id) } : {}),
        }
      }));
    }
    if (Array.isArray(payload?.references?.media_activity_ids)) {
      window.dispatchEvent(new CustomEvent('regimen:plan:updated', {
        detail: { kind: 'media_requirements', activityIds: payload.references.media_activity_ids },
      }));
    }
    console.info('[BackendService] strategy proposal request succeeded', {
      url,
      action,
      proposalId: String(proposalId),
      status: response.status,
      resultStatus: payload?.status || null
    });
    return payload;
  }

  async approveStrategyProposal(proposalId) {
    return this.performStrategyProposalAction(proposalId, 'approve');
  }

  async rejectStrategyProposal(proposalId, comment) {
    return this.performStrategyProposalAction(proposalId, 'reject', comment);
  }

  async acknowledgeStrategyProposal(proposalId) {
    return this.performStrategyProposalAction(proposalId, 'acknowledge');
  }

  async commentOnStrategyProposal(proposalId, comment) {
    return this.performStrategyProposalAction(proposalId, 'comment', comment);
  }

  /**
   * Get the user's currently active strategy
   * @returns {Object|null} The active strategy or null
   */
  async fetchActiveStrategy() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/active`, {
        method: 'GET',
        headers
      });
      if (response.status === 204) return null;
      if (response.ok) return await response.json();
      return null;
    } catch (error) {
      console.error('Error fetching active strategy:', error);
      return null;
    }
  }

  /**
   * Get all strategies (history)
   * @returns {Array} List of strategies
   */
  async fetchAllStrategies() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/all`, {
        method: 'GET',
        headers
      });
      if (response.ok) return await response.json();
      return [];
    } catch (error) {
      console.error('Error fetching strategies:', error);
      return [];
    }
  }

  /**
   * Get a specific strategy by ID
   * @param {string} strategyId
   * @returns {Object|null}
   */
  async fetchStrategy(strategyId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/${strategyId}`, {
        method: 'GET',
        headers
      });
      if (response.ok) return await response.json();
      return null;
    } catch (error) {
      console.error('Error fetching strategy:', error);
      return null;
    }
  }

  /**
   * Kick off async strategy generation and resolve with the generated
   * strategy when the background job finishes. The backend runs the
   * Opus call as a background job (POST returns 202 with a job doc; we
   * poll until status === 'completed'). Returns the strategy doc.
   *
   * @param {Object} options
   * @param {string} [options.user_message]
   * @param {string} [options.conversation_id]
   * @param {(job: Object) => void} [options.onProgress] - called on each
   *   poll with the latest job doc. Use to render multi-step progress UI.
   */
  async generateStrategy(options = {}) {
    const { onProgress, ...body } = options || {};
    try {
      const headers = await this.getAuthHeaders();
      const startResp = await this.fetchApi(`${API_BASE_URL}/api/strategy/generate`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      await publishAiBudgetRequiredFromFetch(startResp);
      if (!startResp.ok) {
        let message = `Strategy generation failed: ${startResp.status}`;
        try {
          const errBody = await startResp.json();
          if (errBody && (errBody.message || errBody.error)) message = errBody.message || errBody.error;
        } catch {}
        throw new Error(message);
      }
      const job = await startResp.json();
      const jobId = job && job._id;
      if (!jobId) throw new Error('Strategy generation did not return a job id');

      if (typeof onProgress === 'function') {
        try { onProgress(job); } catch {}
      }

      // Poll. Strategy gen typically completes in 30-90s; allow up to 5 min.
      const pollIntervalMs = 1500;
      const maxAttempts = Math.ceil((5 * 60 * 1000) / pollIntervalMs);
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((r) => setTimeout(r, pollIntervalMs));
        const pollHeaders = await this.getAuthHeaders();
        const pollResp = await this.fetchApi(`${API_BASE_URL}/api/strategy/jobs/${jobId}`, {
          method: 'GET',
          headers: pollHeaders
        });
        if (!pollResp.ok) {
          if (pollResp.status >= 500) continue;
          throw new Error(`Strategy job poll failed: ${pollResp.status}`);
        }
        const doc = await pollResp.json();
        if (typeof onProgress === 'function') {
          try { onProgress(doc); } catch {}
        }
        if (doc.status === 'completed') {
          return doc.result;
        }
        if (doc.status === 'failed') {
          throw new Error(doc.error || 'Strategy generation failed');
        }
      }
      throw new Error('Strategy generation timed out');
    } catch (error) {
      console.error('Error generating strategy:', error);
      throw error;
    }
  }

  /**
   * Accept a draft strategy: activates it AND (re)generates the user's
   * plan to align with the strategy's current phase. Runs as a background
   * job (POST returns 202 with a job doc; we poll until completed).
   *
   * @param {string} strategyId
   * @param {Object} options
   * @param {boolean} [options.replace_existing] - opt-in to overwriting
   *   an existing plan's workouts. The caller is responsible for asking
   *   the user before passing `true`.
   * @param {(job: Object) => void} [options.onProgress] - called on each
   *   poll with the latest job doc. Use to render a multi-step timeline.
   * @returns {Promise<{strategy: Object, plan_id: string, created_plan: boolean}>}
   */
  async acceptStrategy(strategyId, options = {}) {
    const { onProgress, ...body } = options || {};
    try {
      const headers = await this.getAuthHeaders();
      const startResp = await this.fetchApi(`${API_BASE_URL}/api/strategy/${strategyId}/accept`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      await publishAiBudgetRequiredFromFetch(startResp);
      if (!startResp.ok) {
        let message = `Strategy acceptance failed: ${startResp.status}`;
        try {
          const errBody = await startResp.json();
          if (errBody && (errBody.message || errBody.error)) message = errBody.message || errBody.error;
        } catch {}
        throw new Error(message);
      }
      const job = await startResp.json();
      const jobId = job && job._id;
      if (!jobId) throw new Error('Strategy acceptance did not return a job id');

      if (typeof onProgress === 'function') {
        try { onProgress(job); } catch {}
      }

      // Plan generation can take a minute or two for many sessions.
      const pollIntervalMs = 1500;
      const maxAttempts = Math.ceil((5 * 60 * 1000) / pollIntervalMs);
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((r) => setTimeout(r, pollIntervalMs));
        const pollHeaders = await this.getAuthHeaders();
        const pollResp = await this.fetchApi(`${API_BASE_URL}/api/strategy/jobs/${jobId}`, {
          method: 'GET',
          headers: pollHeaders
        });
        if (!pollResp.ok) {
          if (pollResp.status >= 500) continue;
          throw new Error(`Strategy acceptance poll failed: ${pollResp.status}`);
        }
        const doc = await pollResp.json();
        if (typeof onProgress === 'function') {
          try { onProgress(doc); } catch {}
        }
        if (doc.status === 'completed') return doc.result;
        if (doc.status === 'failed') throw new Error(doc.error || 'Strategy acceptance failed');
      }
      throw new Error('Strategy acceptance timed out');
    } catch (error) {
      console.error('Error accepting strategy:', error);
      throw error;
    }
  }

  /**
   * Activate a draft strategy
   * @param {string} strategyId
   * @returns {Object} The activated strategy
   */
  async activateStrategy(strategyId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/${strategyId}/activate`, {
        method: 'POST',
        headers
      });
      if (response.ok) return await response.json();
      throw new Error(`Strategy activation failed: ${response.status}`);
    } catch (error) {
      console.error('Error activating strategy:', error);
      throw error;
    }
  }

  /**
   * Review the active strategy (AI evaluates progress).
   * Runs as a background job server-side because the LLM call regularly
   * exceeds Heroku's 30s router timeout. POST returns 202 with a job doc;
   * we poll /strategy/jobs/:id until completion (mirrors acceptStrategy).
   *
   * @param {string} strategyId
   * @param {string} userMessage - Optional message to the AI
   * @returns {Object} Review result (the same shape reviewStrategy used to
   *   return synchronously)
   */
  async reviewStrategy(strategyId, userMessage) {
    try {
      const headers = await this.getAuthHeaders();
      const startResp = await this.fetchApi(`${API_BASE_URL}/api/strategy/${strategyId}/review`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_message: userMessage })
      });
      await publishAiBudgetRequiredFromFetch(startResp);
      if (!startResp.ok) {
        let message = `Strategy review failed: ${startResp.status}`;
        try {
          const errBody = await startResp.json();
          if (errBody && (errBody.message || errBody.error)) message = errBody.message || errBody.error;
        } catch {}
        throw new Error(message);
      }
      const job = await startResp.json();
      const jobId = job && job._id;
      if (!jobId) throw new Error('Strategy review did not return a job id');

      // Review usually completes in 30-60s. Allow up to 5 min for safety.
      const pollIntervalMs = 1500;
      const maxAttempts = Math.ceil((5 * 60 * 1000) / pollIntervalMs);
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((r) => setTimeout(r, pollIntervalMs));
        const pollHeaders = await this.getAuthHeaders();
        const pollResp = await this.fetchApi(`${API_BASE_URL}/api/strategy/jobs/${jobId}`, {
          method: 'GET',
          headers: pollHeaders
        });
        if (!pollResp.ok) {
          if (pollResp.status >= 500) continue;
          throw new Error(`Strategy review poll failed: ${pollResp.status}`);
        }
        const doc = await pollResp.json();
        if (doc.status === 'completed') return doc.result;
        if (doc.status === 'failed') throw new Error(doc.error || 'Strategy review failed');
      }
      throw new Error('Strategy review timed out');
    } catch (error) {
      console.error('Error reviewing strategy:', error);
      throw error;
    }
  }

  /**
   * Apply (or cancel) the pending plan-shape changes that the most recent
   * strategy review deferred. Use after a coach review returned
   * `review.pending_plan_changes`.
   *
   * @param {{ scope?: string, mode: 'add'|'replace'|'cancel' }} opts
   * @returns {Promise<object>} { ok, applied, scope, mode, result? }
   */
  async applyPendingPlanChanges({ scope = 'nutrition', mode, change_id, strategy_id, strategy_revision, target_meal_count } = {}) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/active/apply-pending-plan-changes`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, mode, change_id, strategy_id, strategy_revision, target_meal_count })
      });
      if (!response.ok) {
        let msg = `HTTP ${response.status}`;
        try { const j = await response.json(); if (j?.error) msg = j.error; } catch (_) {}
        throw new Error(msg);
      }
      const payload = await response.json();
      if (payload?.applied) {
        window.dispatchEvent(new CustomEvent('regimen:plan:updated', {
          detail: { kind: 'nutrition_schedule', scope, mode }
        }));
        if (payload.change_id || change_id) {
          window.dispatchEvent(new CustomEvent('coach-action-resolved', {
            detail: { change_id: payload.change_id || change_id, status: 'applied' }
          }));
        }
      }
      return payload;
    } catch (error) {
      console.error('Error applying pending plan changes:', error);
      throw error;
    }
  }

  /**
   * Archive (discard) a strategy
   * @param {string} strategyId
   * @returns {boolean} Success
   */
  async archiveStrategy(strategyId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/${strategyId}/archive`, {
        method: 'POST',
        headers
      });
      return response.ok;
    } catch (error) {
      console.error('Error archiving strategy:', error);
      return false;
    }
  }

  /**
   * Permanently delete a non-active strategy.
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  async deleteStrategy(strategyId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await this.fetchApi(`${API_BASE_URL}/api/strategy/${strategyId}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) {
        let error = `HTTP ${response.status}`;
        try { const body = await response.json(); if (body?.error) error = body.error; } catch (_) {}
        return { ok: false, error };
      }
      return { ok: true };
    } catch (error) {
      console.error('Error deleting strategy:', error);
      return { ok: false, error: error.message || 'Failed to delete strategy' };
    }
  }

  // Push Health Connect records to the backend.
  // records: [{ date: 'YYYY-MM-DD', weight_kg?, sleep_minutes? }]
  async syncFromHealthConnect(records) {
    try {
      const response = await this.client.post(
        `${API_BASE_URL}/api/metrics/sync-from-health-connect`,
        { records },
        { withCredentials: true,
          katafitEventIdentity: this.authenticatedIdentity,
          katafitEventOwner: this.positionOwner,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error syncing from Health Connect:', error);
      throw error;
    }
  }

  // Mint a single-use Clerk sign-in ticket for the current user so the
  // native Android app can claim the same session inside its WebView.
  // Used by the Custom Tabs OAuth bridge (see NativeGoogleSignIn).
  async getMobileSignInTicket() {
    const authHeaders = await this.getAuthHeaders();
    const response = await this.fetchApi(`${API_BASE_URL}/api/auth/mobile-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    });
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try { const body = await response.json(); if (body?.error) msg = body.error; } catch (_) {}
      throw new Error(msg);
    }
    return response.json(); // { ticket }
  }
}

export default BackendService;
